import { mcpBus } from '@/mcp/studio-mcp-bus'
import { useStudioStore } from '@/store/studio-store'
import { getStudioAccessToken } from '@/services/auth-provider'
import type { DesignSystemState } from '@/features/design-system'
import {
  clearStoredAppGenerationJob,
  fingerprintAppGenerationSpec,
  readStoredAppGenerationJob,
  type AppGenerationJobDescriptor,
  type AppSpecFull,
  type StudioImageEvidenceSourceInput,
} from '@/services/block-api-service'
import type {
  ImageSourceEvidence,
  PixelRagVisualSegment,
} from '@cogo/headless-core/image-evidence-verification'
import {
  deleteProject as deleteServerProject,
  loadProject,
  markProjectGenerationFinalizationFailed,
  patchProjectSettings,
  saveProjectMeta,
} from '@/services/project-api'
import { slugDesignSystemId } from '@/features/design-system/lib/design-system-export'
import { buildPendingGenerationMarker } from '@/features/project/lib/project-generation-durability'
import {
  buildLibraryStructureSelection,
  type LibraryStructureSelection,
} from '@/features/project/lib/library-structure-selection'
import {
  fetchLibraryStructureCandidates,
  type LibraryStructureCandidate,
} from './library-structure-lookup'
import { parseLegacyScreenReadback } from './legacy-screen-readback'
import {
  assertGenuineGenerationTrigger,
  type GenerationTriggerProvenance,
} from './studio-creation-router'
import { buildAsIsSnapshotFromLegacyScreen } from './legacy-screen-as-is-capture'
import type { AsIsSnapshot } from '@/features/preview/services/as-is-snapshot'
import {
  assertCreatorGenerationDesignSystemAuthority,
  resolveCreatorDefaultDesignSystemBinding,
  type CreatorDefaultDesignSystemBinding,
} from './creator-design-system-binding'
import {
  withDesignSystemPackReleaseGenerationAuthority,
  type StudioDesignSystemPackReleaseBinding,
} from '@/features/design-system/lib/design-system-pack-release-binding'
import {
  ProjectDesignSystemGenerationBindingError,
  describeProjectDesignSystemGenerationBlocker,
  resolveProjectDesignSystemGenerationBinding,
  withProjectDesignSystemGenerationAuthority,
  type ProjectDesignSystemGenerationBindingErrorCode,
  type StudioProjectDesignSystemGenerationBinding,
} from '@/features/design-system/lib/project-design-system-generation-binding'
import {
  appTargetFromRuntimeTargets,
  deriveStudioProjectPlanFromGeneration,
  preservesSelectedDesignSystemRelease,
  STUDIO_PLANNING_METADATA_SETTINGS_KEY,
  STUDIO_PROJECT_PLAN_SETTINGS_KEY,
  withStudioPlanningMetadataSettings,
  withStudioProjectPlanSettings,
  type StudioDecomposePlanningMetadata,
  type StudioProjectPlan,
} from '@/features/project/lib/studio-project-plan'
import {
  buildAppPlanFromDecomposeApi,
  type DecomposePlanningProgress,
  type PromptAppPlan,
} from './app-spec-from-prompt'
import {
  prepareChatGeneratedApplicationDraft,
  type ChatGeneratedApplicationDraftPreparationReceipt,
} from './chat-generated-application-draft-api'
import {
  chatGenerationDraftAttemptForRequestContent,
  completeChatGenerationDraftAttempt,
  normalizeChatGenerationRequestContent,
} from './chat-generation-draft-attempt'
import {
  APP_GENERATION_ONTOLOGY_BINDING_PARAM_KEY,
  parseApplicationOntologyGenerationBootstrapInput,
  type ApplicationOntologyGenerationBootstrapInput,
} from '@/features/application-ontology/application-ontology-generation'
import {
  livePlannerCreatingProjectMessage,
  livePlannerFailureMessage,
  livePlannerStageMessage,
} from './live-planner-copy'
import { evaluateChatOntologyGenerationQuality } from './chat-ontology-generation-quality'
import { registerChatStartedAppInLibrary } from './chat-library-registration'
import {
  cogoXyzRegistry,
  type CogoXyzRegistrySnapshot,
} from '@/features/cogo-xyz/cogo-xyz-registry'
import { COGO_XYZ_PROJECT_SETTINGS_KEY } from '@/features/cogo-xyz/cogo-xyz-project-storage'
import { pushCogoXyzRegistrySnapshotToServer } from '@/features/cogo-xyz/cogo-xyz-server-sync'
import { withApplicationResponsibilityDecisions } from './application-responsibility'
import { withStudioOntologyRetrievalTrace } from './ontology-retrieval-evidence'
import { withActionFlowRuntimeSimulationProof } from './actionflow-runtime-simulation'
import { withStudioReportApplicationIntegration } from './report-application-integration'
import { withImageEvidenceVerificationShadow } from './image-evidence-shadow'
import {
  evaluateGenerationEntrySourceSufficiency,
  type GenerationEntrySourceSufficiencyDecision,
} from './generation-entry-sufficiency'
import { buildTemplateGenerationModelPolicy } from './template-generation-model-policy'
import {
  CandidateLifecycleAuthorityUncertainError,
  CandidateLifecycleTerminationError,
  createCandidateGenerationLifecycle,
  type CandidateGenerationLifecycleController,
  type CandidateGenerationLifecycleInput,
} from './candidate-generation-lifecycle'
import {
  STUDIO_CANDIDATE_LIFECYCLE_DESCRIPTOR_VERSION,
  parseStudioCandidateLifecycleDescriptor,
  withCandidateLifecycleDescriptorSettings,
  type StudioCandidateLifecycleDescriptor,
} from '@/features/productization/candidate-verification-model'
import { createStudioCandidateEntryEvidence } from '@/features/productization/candidate-lifecycle-entry-evidence'
import {
  STUDIO_FEATURED_OUTCOME_SOURCE,
  STUDIO_NEWSLETTER_MEMBERSHIP_SKU_ID,
  STUDIO_RESERVATION_OPERATIONS_SKU_ID,
  assertStudioOutcomeGenerationAuthority,
  parseStudioOutcomeIntent,
  type StudioOutcomeIntent,
} from '@/features/productization/studio-outcome-catalog'
import {
  claimStudioGenerationRequest,
  releaseStudioGenerationRequest,
} from '@/features/productization/studio-featured-outcome-draft'
import { getLocale } from '@/pages/cogo-marketing/i18n/locale-store'
import { STUDIO_DICTIONARIES } from '@/i18n/studio-dictionaries'
import {
  StudioSourceBoundGenerationUnavailableError,
  withStudioSourceDesignSystemGenerationContract,
  type StudioSourceDesignSystemGenerationContext,
} from './source-design-system-generation-bridge'
import type { OpenPencilSourceWorkspace } from '@/features/openpencil-figma/openpencil-source-workspace'
import { saveOpenPencilSourceWorkspaceToCurrentProject } from '@/features/openpencil-figma/openpencil-source-workspace-storage'
import { rescopeOpenPencilSourceRightsDeclarationProject } from '@cogo/source-openpencil'
import {
  StudioLibraryDesignOntologyBindingError,
  bindStudioLibraryDesignOntology,
  bindStudioLibraryRagResolution,
  projectStudioLibraryDesignBoundAppSpec,
  type StudioLibraryDesignOntologyBindingInput,
} from './studio-library-design-ontology-binding'
import type { LibraryRagOrchestrationResult } from '@cogo/agentic-generator/library-rag-agent-orchestration'
import type { ReadinessReport } from '@/features/generation-readiness/check-readiness'
import { checkStudioGenerationReadiness } from '@/features/generation-readiness/studio-readiness-check'
import {
  STUDIO_GENERATION_DESIGN_AXES,
  buildStudioGenerationAuthorityPreflight,
  finalizeStudioGenerationAuthority,
  type StudioGenerationAuthorityEnvelope,
} from '@/features/generation-authority/generation-authority-envelope'
import {
  mergePlannerCandidateIntoOpenSlots,
  promptAppPlanFromProvidedAppSpec,
  validatePartialProvidedApplicationAuthority,
  type StudioProvidedApplicationAuthority,
} from '@/features/generation-authority/application-authority'
import {
  resolveProductionStudioExactGenerationAuthority,
  type StudioExactGenerationAuthority,
} from '@/features/generation-authority/exact-generation-authority'
import {
  buildStudioGenerationAuthorityInput,
  normalizeStudioGenerationEntryFacts,
  type StudioGenerationEntry,
} from '@/features/generation-authority/generation-entry-authority'
import type { AxisName } from '@/features/design-system/source-derived/types'

export {
  STUDIO_FEATURED_OUTCOME_SOURCE,
  STUDIO_NEWSLETTER_MEMBERSHIP_SKU_ID,
  STUDIO_RESERVATION_OPERATIONS_SKU_ID,
}
export type { StudioOutcomeIntent }

const GENERATED_APP_COPY_LANGUAGE = {
  en: 'English',
  ko: 'Korean',
  ja: 'Japanese',
  zh: 'Simplified Chinese',
  'zh-TW': 'Traditional Chinese',
} as const

function withGeneratedApplicationCopyLocale(
  appSpec: AppSpecFull,
  locale: ReturnType<typeof getLocale>,
): AppSpecFull {
  const language = GENERATED_APP_COPY_LANGUAGE[locale]
  const instruction =
    `User-visible copy language: ${language} (${locale}). ` +
    `Render every user-visible title, label, placeholder, button, empty state, ` +
    `validation message, and sample content in ${language}. ` +
    'Keep code identifiers unchanged.'

  return {
    ...appSpec,
    blocks: appSpec.blocks.map((block) => ({
      ...block,
      description: `${block.description.trim()} ${instruction}`.trim(),
    })),
  }
}

/**
 * G3 — outcome of the U2 library-lookup judgment moment. `start-fresh`
 * proceeds exactly like the pre-G3 creator path (G2 default DS binding);
 * `library-candidate` records the chosen admitted asset as project lineage.
 */
export type LibraryStructureSelectionChoice =
  | { kind: 'start-fresh' }
  | { kind: 'library-candidate'; candidate: LibraryStructureCandidate }

export type { LibraryStructureCandidate }

/**
 * FINDING-1 (adversarial review 2026-07-27) — upper bound on the U2 judgment
 * wait. The module-global generation lease (claimStudioGenerationRequest) is
 * held for the whole generation request, INCLUDING this human judgment
 * moment; an unattended card prompt must therefore never hold the lease
 * open-endedly, or every later generation submit in the session would be
 * rejected as busy. 90s is deliberately generous for reading up to three
 * cards (this is a judgment moment, not a confirm dialog) but finite: on
 * expiry the flow auto-resolves to 'start-fresh' — the exact pre-G3 path —
 * and fires the dismiss signal so the presenting surface drops its cards.
 */
export const LIBRARY_STRUCTURE_JUDGMENT_TIMEOUT_MS = 90_000

/** G3 — context handed to the judgment surface alongside the candidates. */
export interface LibraryStructureJudgmentContext {
  /**
   * Fires when the judgment moment is over without the surface's own
   * resolution — bounded-wait expiry, request abort, or any other
   * producer-side settlement. The presenting surface must dismiss its cards:
   * the generation flow has already moved on, and stale cards would
   * misrepresent a live judgment.
   */
  dismissSignal: AbortSignal
}

export interface StartAppGenerationOptions {
  /** UI provenance only; never changes authority classification facts. */
  generationEntry?: StudioGenerationEntry
  designSystemPreset?: DesignSystemState | null
  /** Explicit project-start contract selected in the New Project wizard. */
  studioProjectPlan?: StudioProjectPlan
  registerLibraryItem?: boolean
  /**
   * `details.ontologyIssueCodes` carries the issue CODES the admission
   * check already computed. Without them the creator surface has only the
   * joined prose, and substring-matching it collapsed every ontology
   * rejection into one message that names the library as the cause.
   */
  onGenerationError?: (
    message: string,
    details?: { ontologyIssueCodes?: readonly string[] },
  ) => void
  signal?: AbortSignal
  /** Internal owner token for a generation lease already claimed by a Studio surface. */
  generationLeaseId?: string
  planningTimeoutMs?: number
  onPlanningProgress?: (progress: DecomposePlanningProgress) => void
  imageEvidenceVerificationEnabled?: boolean
  imageSourceEvidence?: ImageSourceEvidence
  imageEvidenceSource?: StudioImageEvidenceSourceInput
  pixelRagVisualSegments?: PixelRagVisualSegment[]
  inferredHiddenTruths?: string[]
  sourceProject?: unknown
  onClarificationRequired?: (
    decision: GenerationEntrySourceSufficiencyDecision,
  ) => void
  /**
   * G3 — U2 judgment moment. When the plain creator path (no library/source/
   * outcome/wizard authority) finds admitted library structure candidates
   * AFTER planning succeeds and BEFORE the server project is created, this
   * callback presents them (chat/hero judgment surface) and resolves with the
   * user's choice. Absent callback, empty candidates, or a lookup failure all
   * proceed exactly as the pre-G3 path — selection is never a gate. The wait
   * for the returned promise is bounded (LIBRARY_STRUCTURE_JUDGMENT_TIMEOUT_MS,
   * FINDING-1): on expiry the flow proceeds start-fresh and
   * `judgment.dismissSignal` fires so the surface must drop its cards.
   */
  onLibraryStructureSelection?: (
    candidates: LibraryStructureCandidate[],
    judgment: LibraryStructureJudgmentContext,
  ) => Promise<LibraryStructureSelectionChoice>
  candidateLifecycle?: Omit<
    CandidateGenerationLifecycleInput,
    | 'projectId'
    | 'sourceAssessmentRevision'
    | 'beforeCreateJournal'
    | 'beforeRecordDemand'
    | 'signal'
  >
  outcomeIntent?: StudioOutcomeIntent
  sourceDesignSystemGeneration?: StudioSourceDesignSystemGenerationContext
  /**
   * F5 (WonBanking arc) — explicit, opt-in Design System PACK release
   * authority: a binding already verified fail-closed by
   * `resolveDesignSystemPackReleaseBinding` (content/package hash recomputed,
   * releaseId pinned). When present it is the SOLE design authority for the
   * generation: it is rejected in combination with library bindings, source
   * workspaces/contexts, outcome intents, wizard project plans, candidate
   * lifecycles, and explicit presets, and it suppresses the G2 creator
   * default. It is NEVER auto-applied — callers must thread it explicitly.
   */
  designSystemPackRelease?: StudioDesignSystemPackReleaseBinding
  /** Complete, partial, or locked application authority supplied by the user. */
  applicationAuthority?: StudioProvidedApplicationAuthority
  /** One normalized exact source/design/application/rights contract. */
  exactGenerationAuthority?: StudioExactGenerationAuthority
  /**
   * fix-round-1 (Task 3) — the ACTUAL pasted legacy screen, verbatim, before
   * `confirmIntakeCards` (hero-chat-input.tsx) augments it with the
   * human-confirmed decision block and, on a multi-document paste, joins it
   * with every other open card's segment. `prompt` at the call site is that
   * AUGMENTED text, not the original, so `deriveAsIsSnapshotFromPrompt` must
   * not use `prompt` as the As-Is markup source when this is present —
   * rendering our own generated decision prose (or another document
   * entirely) inside a frame labelled "원본" is exactly the dishonesty the
   * As-Is tab exists to prevent. Absent (every other caller, and a plain
   * paste with no card confirmed), `prompt` IS the original and remains the
   * fallback markup source — unchanged from before this fix.
   */
  originalPastedMarkup?: string
  /**
   * Generic Application Ontology Program — Studio Task 2.
   *
   * The ONLY generation-binding input a public caller may give: five fields,
   * all of them things the project or the user already chose. Everything the
   * evidence chain needs beyond these — the profile SELECTION record, the
   * attempt, the KG binding, the receipt, the chain head, the eligibility and
   * the admission — is resolved or minted server-side, by platform, and can
   * neither be supplied nor observed here.
   *
   * Its presence (never the profile's identity) is what makes a generation
   * package-bound and therefore fail-closed.
   */
  applicationOntologyBootstrap?: ApplicationOntologyGenerationBootstrapInput
  /**
   * Optional, defense-in-depth: proof (studio-creation-router.ts,
   * `mintClassifierDispatchTrigger`) that this call traces back to the
   * deterministic `routeStudioCreationRequest` classifier's read of the
   * user's own typed prompt (2026-08-17 wave-4 lane-2). Absent for every
   * pre-existing caller that does not go through the chat/classifier
   * surface (the New Project wizard's `studioProjectPlan`, the dev
   * pack-release trigger's `designSystemPackRelease`, cogo-xyz's source
   * import, the library entry points) -- their own, separately-verified
   * authority is unaffected and unchanged by this field.
   *
   * When present, it MUST be genuine: a forged claim fails closed
   * (`GenerationTriggerProvenanceError`) before `mcpBus.execute` runs. Use
   * `startAppGenerationFromClassifiedPrompt` below to make this REQUIRED
   * for a classifier-driven call site instead of merely verified-if-present.
   */
  trigger?: GenerationTriggerProvenance
}

/**
 * The private, post-admission members a public caller may never set.
 *
 * Rejected outright rather than stripped: a field that is silently ignored is
 * indistinguishable, from the caller's side, from a field that was honoured.
 */
const FORBIDDEN_PUBLIC_APPLICATION_ONTOLOGY_KEYS = [
  'applicationOntologyGeneration',
  'applicationOntologyContext',
  'profileSelectionRef',
  'attempt',
  'attemptId',
  'kgPreflight',
  'kgPreflightReceiptRef',
  'receiptChainHeadRef',
  'applicationGenerationEligibilityRef',
  'eligibilityRef',
  'admissionRef',
] as const

export class StudioApplicationOntologyAuthorityRejected extends Error {
  override readonly name = 'StudioApplicationOntologyAuthorityRejected'
  readonly rejectedKey: string

  constructor(rejectedKey: string) {
    super(`application-ontology-generation-authority-not-caller-supplied:${rejectedKey}`)
    this.rejectedKey = rejectedKey
  }
}

function assertApplicationOntologyBootstrapIsPublicOnly(
  options: StartAppGenerationOptions,
): void {
  const record = options as unknown as Record<string, unknown>
  for (const key of FORBIDDEN_PUBLIC_APPLICATION_ONTOLOGY_KEYS) {
    if (record[key] !== undefined) {
      throw new StudioApplicationOntologyAuthorityRejected(key)
    }
  }
  if (options.applicationOntologyBootstrap === undefined) return
  // The same closed five-field guard the server re-applies. A sixth field is
  // refused here, at the public entry, so it never reaches the wire.
  if (
    parseApplicationOntologyGenerationBootstrapInput(
      options.applicationOntologyBootstrap,
    ) === null
  ) {
    throw new StudioApplicationOntologyAuthorityRejected(
      'applicationOntologyBootstrap',
    )
  }
}

export interface StartLibraryApplicationGenerationOptions
  extends Omit<
    StartAppGenerationOptions,
    'outcomeIntent' | 'designSystemPackRelease'
  > {
  libraryDesignOntologyBinding: StudioLibraryDesignOntologyBindingInput
  outcomeIntent?: never
  designSystemPackRelease?: never
}

export interface StartLibraryRagApplicationGenerationOptions
  extends Omit<
    StartAppGenerationOptions,
    | 'outcomeIntent'
    | 'designSystemPreset'
    | 'sourceDesignSystemGeneration'
    | 'designSystemPackRelease'
  > {
  libraryRagResolution: Extract<LibraryRagOrchestrationResult, { ok: true }>
  outcomeIntent?: never
  designSystemPreset?: never
  sourceDesignSystemGeneration?: never
  designSystemPackRelease?: never
}

interface InternalStartAppGenerationOptions extends StartAppGenerationOptions {
  libraryDesignOntologyBinding?: StudioLibraryDesignOntologyBindingInput
  libraryRagResolution?: Extract<LibraryRagOrchestrationResult, { ok: true }>
  /** Stable authenticated scope for one Chat retry identity. */
  chatGenerationAttemptScope?: string
}

export interface StartedCandidateGenerationLifecycle {
  projectId: string
  skuId: string
  candidateId: string
  journalRunId: string
  candidateRevision: string
  assessmentRevision: string | null
  verify: CandidateGenerationLifecycleController['verify']
}

export interface StartedAppGeneration {
  status: 'started'
  projectId: string
  appSpec: AppSpecFull
  generation: Promise<void>
  sourceSufficiency: GenerationEntrySourceSufficiencyDecision
  generationAuthority: StudioGenerationAuthorityEnvelope
  candidateLifecycle?: StartedCandidateGenerationLifecycle
}

export interface AppGenerationClarificationRequired {
  status: 'clarification-required'
  sourceSufficiency: GenerationEntrySourceSufficiencyDecision
}

/**
 * Task 4 (ontology-ux-contract cycle) — the pre-generation readiness gate's
 * refusal shape. Mirrors `AppGenerationClarificationRequired` structurally
 * (a status literal plus the one payload the caller needs), but is a
 * distinct status rather than an overload of `'clarification-required'`:
 * the two gates ask genuinely different questions (source evidence
 * sufficiency vs. the full `ReadinessReport` — design-system axes plus any
 * machine-validated gap) and conflating them under one status would hide
 * which one actually fired.
 */
export interface AppGenerationReadinessBlocked {
  status: 'generation-readiness-blocked'
  readiness: ReadinessReport
}

export type StartAppGenerationOutcome =
  | StartedAppGeneration
  | AppGenerationClarificationRequired
  | AppGenerationReadinessBlocked

export class StudioGenerationBusyError extends Error {
  override readonly name = 'StudioGenerationBusyError'
  readonly userMessage: string

  constructor() {
    const userMessage =
      STUDIO_DICTIONARIES[getLocale()].home.featuredOutcome.inProgress
    super(userMessage)
    this.userMessage = userMessage
  }
}

async function ensureStudioGenerationToolsRegistered(): Promise<void> {
  if (typeof window === 'undefined') return
  if (
    typeof (mcpBus as unknown as { registerTool?: unknown }).registerTool !==
    'function'
  )
    return
  try {
    const { registerStudioTools } = await import('@/mcp/register-studio-tools')
    // Generic Application Ontology Program — wiring lane A1, seam 2. Same
    // resolver as the bridge bootstrap: `registerStudioTools` registers once,
    // and whichever of the two sites runs first decides what the artifact
    // persistence gate is given. Passing the port at only one of them makes
    // the gate's presence depend on the race.
    const { resolveStudioToolRegistrationOptions } = await import(
      '@/mcp/studio-tool-registration-options'
    )
    registerStudioTools(resolveStudioToolRegistrationOptions())
  } catch (err) {
    console.warn(
      '[start-app-generation] Failed to ensure Studio MCP tools are registered',
      err,
    )
  }
}

function generationTargetPlatformFromPlan(
  plan: StudioProjectPlan,
): NonNullable<AppSpecFull['generationTargetPlatform']> {
  return plan.runtimeTargets.includes('flutter') ||
    plan.runtimeTargets.includes('mobile')
    ? 'flutter'
    : 'react'
}

function withProjectPlanMetadata(
  appSpec: AppSpecFull,
  studioProjectPlan: StudioProjectPlan,
  planningMetadata: StudioDecomposePlanningMetadata,
): AppSpecFull {
  return {
    ...appSpec,
    appClass: appSpec.appClass ?? studioProjectPlan.appClass,
    targetPlatform: appTargetFromRuntimeTargets(
      studioProjectPlan.runtimeTargets,
    ),
    generationTargetPlatform:
      appSpec.generationTargetPlatform ??
      generationTargetPlatformFromPlan(studioProjectPlan),
    runtimeTargets: studioProjectPlan.runtimeTargets,
    projectPlan: studioProjectPlan,
    adaptiveRuntime: studioProjectPlan.adaptiveRuntime,
    planningSource: planningMetadata.planningSource,
    decomposePlanning: planningMetadata,
  }
}

interface ValidatedSourceWorkspaceTransfer {
  sourceProjectId: string | null
  workspace: OpenPencilSourceWorkspace
}

async function bindValidatedSourceWorkspaceToCreatedProject(input: {
  projectId: string
  transfer: ValidatedSourceWorkspaceTransfer
}): Promise<void> {
  const current = useStudioStore.getState()
  if (current.currentProjectId !== input.projectId || !current.currentProject) {
    throw new Error(
      'Studio source/design-system generation blocked: the created project is not the current loaded project.',
    )
  }
  if (
    current.openPencilSourceWorkspace &&
    current.openPencilSourceWorkspace !== input.transfer.workspace
  ) {
    throw new Error(
      'Studio source/design-system generation blocked: the created project already has different OpenPencil source authority.',
    )
  }

  // The rights declaration's substance is bound to the SOURCE (sourceId +
  // fileSha256 + sourceRevision) and grants nothing; scope.projectId is
  // administrative. Persisting it still scoped to the source project would
  // make the per-project workspace loader silently drop the evidence on the
  // next reload, so this host-controlled transfer re-scopes it to the created
  // project and recomputes the integrity hash. Attestation content
  // (declarant, declaredAt, review, authority) is preserved unchanged.
  const transferredWorkspace: OpenPencilSourceWorkspace = input.transfer
    .workspace.sourceRightsDeclaration
    ? {
        ...input.transfer.workspace,
        sourceRightsDeclaration:
          await rescopeOpenPencilSourceRightsDeclarationProject(
            input.transfer.workspace.sourceRightsDeclaration,
            input.projectId,
          ),
      }
    : input.transfer.workspace

  // createServerProject opens the new project and intentionally clears the
  // previous project's source authority. This transfer is the one bounded
  // exception: the exact workspace that already built the trusted generation
  // contract is rebound only to the exact project returned and loaded by that
  // host-controlled creation call.
  current.setOpenPencilSourceWorkspace(transferredWorkspace)

  let rebound = useStudioStore.getState()
  if (
    rebound.currentProjectId !== input.projectId ||
    rebound.openPencilSourceWorkspace !== transferredWorkspace
  ) {
    throw new Error(
      `Studio source/design-system generation blocked: failed to bind source workspace from ${input.transfer.sourceProjectId ?? 'unscoped source'} to created project ${input.projectId}.`,
    )
  }

  await saveOpenPencilSourceWorkspaceToCurrentProject(transferredWorkspace)

  rebound = useStudioStore.getState()
  if (
    rebound.currentProjectId !== input.projectId ||
    rebound.openPencilSourceWorkspace !== transferredWorkspace
  ) {
    throw new Error(
      'Studio source/design-system generation blocked: the active project changed while persisting transferred source authority.',
    )
  }
}

function syncProjectPlanInStore(
  studioProjectPlan: StudioProjectPlan,
  planningMetadata?: StudioDecomposePlanningMetadata,
): void {
  useStudioStore.setState((state) => {
    state.projectPlan = studioProjectPlan
    if (state.projectMeta)
      state.projectMeta.studioProjectPlan = studioProjectPlan
    if (
      state.currentProject?.manifest &&
      typeof state.currentProject.manifest === 'object'
    ) {
      const manifest = state.currentProject.manifest as Record<string, unknown>
      manifest.runtimeTargets = studioProjectPlan.runtimeTargets
      manifest.targetPlatform = appTargetFromRuntimeTargets(
        studioProjectPlan.runtimeTargets,
      )
      manifest.studioProjectPlan = studioProjectPlan
      manifest.adaptiveRuntime = studioProjectPlan.adaptiveRuntime
      if (planningMetadata) {
        manifest.planningSource = planningMetadata.planningSource
        manifest.decomposePlanning = planningMetadata
      } else {
        delete manifest.planningSource
        delete manifest.decomposePlanning
      }
    }
  })
}

function readRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

async function persistGenerationSubjectIdentity(
  projectId: string,
  appId: string,
): Promise<void> {
  const studio = useStudioStore.getState()
  if (
    studio.currentProjectId !== projectId ||
    studio.projectSource !== 'server' ||
    !studio.currentProject
  ) {
    throw new Error(
      'Generated application identity requires the exact open server project.',
    )
  }
  const manifest = readRecord(studio.currentProject.manifest)
  const existingAppId =
    typeof manifest.appId === 'string' && manifest.appId ? manifest.appId : null
  if (existingAppId && existingAppId !== appId) {
    throw new Error(
      'Generated application identity conflicts with the server project manifest.',
    )
  }
  const subjectManifest = { ...manifest, appId }
  await saveProjectMeta(projectId, { manifest: subjectManifest })
  useStudioStore.setState((state) => {
    if (
      state.currentProjectId !== projectId ||
      state.projectSource !== 'server' ||
      !state.currentProject
    ) {
      return
    }
    state.currentProject.manifest = subjectManifest as never
  })
  const current = useStudioStore.getState()
  if (
    current.currentProjectId !== projectId ||
    current.projectSource !== 'server' ||
    !current.currentProject
  ) {
    throw new Error(
      'Generated application identity could not be bound to the active project.',
    )
  }
}

function generationAbortError(): Error {
  const error = new Error('Generation request was cancelled.')
  error.name = 'AbortError'
  return error
}

function throwIfGenerationAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw generationAbortError()
}

/**
 * Every `cleanupCreatedProject` reason code this file emits from an
 * `options.signal?.aborted` branch — i.e. the user cancelled. Enumerated
 * from the actual call sites rather than pattern-matched on a substring:
 *
 *   - 'generation-request-aborted-before-lifecycle'
 *   - 'generation-request-aborted-during-entry-evidence'
 *   - 'generation-request-aborted-during-lifecycle'
 *   - 'generation-request-aborted-after-lifecycle'
 *   - 'generation-request-aborted-before-execution'
 *   - 'generation-request-aborted-during-execution'
 *
 * A cancellation is NOT a finalization failure. The finalization notice
 * tells the user "only the last step in your browser didn't complete" — for
 * a generation the user themselves cancelled, that causal claim is simply
 * wrong, and the preserved project is a normal saved app that needs no
 * notice at all. So the cleanup preserve branch records nothing for these.
 */
const USER_ABORTED_CLEANUP_REASON_CODES: ReadonlySet<string> = new Set([
  'generation-request-aborted-before-lifecycle',
  'generation-request-aborted-during-entry-evidence',
  'generation-request-aborted-during-lifecycle',
  'generation-request-aborted-after-lifecycle',
  'generation-request-aborted-before-execution',
  'generation-request-aborted-during-execution',
])

export function isUserAbortedCleanupReasonCode(reasonCode: string): boolean {
  return USER_ABORTED_CLEANUP_REASON_CODES.has(reasonCode)
}

/**
 * 확정 인테이크 프롬프트에서 as-is를 파생한다.
 *
 * `prompt`는 읽어들일 구성(필드/표/액션)의 출처이자, 골격의 필드/테이블/버튼을
 * 읽어내는 readback 소스로는 항상 쓰인다 — 카드를 고르지 않고 원본만 붙여넣은
 * 사용자도 as-is를 얻는다.
 *
 * 그러나 As-Is 탭에 verbatim으로 실어 보낼 마크업 출처는 다르다. `prompt`는
 * `confirmIntakeCards`(hero-chat-input.tsx)가 붙여넣은 원본에 사람이 확정한
 * 결정 블록을 이어붙인 것이고, 여러 문서를 한 번에 붙여넣었다면 다른 카드의
 * 세그먼트까지 함께 이어붙은 것이다 — 즉 `prompt` 자체는 더 이상 "원본"이
 * 아니다 (fix-round-1, Task 3). `originalPastedMarkupOverride`가 있으면 그것이
 * 실제 붙여넣은 세그먼트이므로 마크업 출처로 우선한다; 없으면(다른 모든
 * 호출자, 카드를 확정하지 않은 평범한 붙여넣기) `prompt`가 곧 원본이므로 그대로
 * 폴백한다 — 이 함수 도입 이전 동작과 동일.
 */
export function deriveAsIsSnapshotFromPrompt(
  prompt: string,
  capturedAt: string,
  originalPastedMarkupOverride?: string,
): AsIsSnapshot | null {
  const readback = parseLegacyScreenReadback(prompt)
  if (!readback) return null
  return buildAsIsSnapshotFromLegacyScreen(
    readback,
    capturedAt,
    originalPastedMarkupOverride ?? prompt,
  )
}

/**
 * Await the U2 judgment while keeping the generation abortable AND bounded
 * (G3 + FINDING-1). Settles in exactly one of three ways: the surface's own
 * choice, an abort (rejects AbortError), or bounded-wait expiry (resolves
 * 'start-fresh' with a breadcrumb). The timer is cancelled on any earlier
 * settlement.
 */
async function awaitLibraryStructureChoice(
  pending: Promise<LibraryStructureSelectionChoice>,
  signal?: AbortSignal,
): Promise<LibraryStructureSelectionChoice> {
  return new Promise<LibraryStructureSelectionChoice>((resolve, reject) => {
    if (signal?.aborted) {
      reject(generationAbortError())
      return
    }
    let settled = false
    const settle = (complete: () => void) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      signal?.removeEventListener('abort', onAbort)
      complete()
    }
    const onAbort = () => settle(() => reject(generationAbortError()))
    const timer = setTimeout(() => {
      settle(() => {
        console.info(
          '[start-app-generation] Library structure judgment window expired; proceeding with the standard creator path (start-fresh).',
        )
        resolve({ kind: 'start-fresh' })
      })
    }, LIBRARY_STRUCTURE_JUDGMENT_TIMEOUT_MS)
    signal?.addEventListener('abort', onAbort, { once: true })
    pending.then(
      (choice) => settle(() => resolve(choice)),
      (cause) => settle(() => reject(cause as Error)),
    )
  })
}

/**
 * G3 — run the U2 library-lookup judgment moment. Returns the selection
 * lineage to persist with the project, or undefined for every "proceed as
 * today" outcome (no candidates, lookup failure, start-fresh choice,
 * unverifiable choice). Only an abort escapes as a throw.
 */
async function resolveLibraryStructureSelection(
  prompt: string,
  onLibraryStructureSelection: NonNullable<
    StartAppGenerationOptions['onLibraryStructureSelection']
  >,
  signal?: AbortSignal,
): Promise<LibraryStructureSelection | undefined> {
  let candidates: LibraryStructureCandidate[] = []
  try {
    candidates = await fetchLibraryStructureCandidates(prompt, {
      limit: 3,
      signal,
    })
  } catch (cause) {
    // fetchLibraryStructureCandidates is fail-open by contract; this guard
    // only protects against a misbehaving test double / future refactor.
    if (signal?.aborted) throw generationAbortError()
    console.info(
      '[start-app-generation] Library structure lookup failed; proceeding with the standard creator path.',
      cause,
    )
    return undefined
  }
  throwIfGenerationAborted(signal)
  if (candidates.length === 0) {
    console.info(
      '[start-app-generation] No admitted library structure candidates; proceeding with the standard creator path.',
    )
    return undefined
  }
  // FINDING-1 — the dismiss channel: whichever way the judgment moment ends
  // (selection, expiry, abort, judgment failure), the `finally` below fires
  // the signal so the presenting surface drops its cards. On selection the
  // surface has already cleared them itself, so the extra fire is a no-op.
  const dismissController = new AbortController()
  let choice: LibraryStructureSelectionChoice
  try {
    choice = await awaitLibraryStructureChoice(
      onLibraryStructureSelection(candidates, {
        dismissSignal: dismissController.signal,
      }),
      signal,
    )
  } catch (cause) {
    if (signal?.aborted || (cause instanceof Error && cause.name === 'AbortError')) {
      throw cause
    }
    console.warn(
      '[start-app-generation] Library structure judgment failed; proceeding without selection lineage.',
      cause,
    )
    return undefined
  } finally {
    dismissController.abort()
  }
  throwIfGenerationAborted(signal)
  if (choice?.kind !== 'library-candidate') return undefined
  // The choice must be one of the candidates actually presented: unverifiable
  // lineage is never persisted — proceed exactly as start-fresh instead.
  const verified = candidates.find(
    (candidate) => candidate.assetId === choice.candidate?.assetId,
  )
  if (!verified) {
    console.warn(
      '[start-app-generation] Library structure choice did not match a presented candidate; proceeding without selection lineage.',
    )
    return undefined
  }
  return buildLibraryStructureSelection(verified)
}

function navigateToGeneratedStudioProject(projectId: string): void {
  const project = useStudioStore.getState()
  if (project.currentProjectId !== projectId) {
    throw new Error(
      'Generated project could not be activated because its editor state is unavailable.',
    )
  }
  useStudioStore.setState((state) => {
    state.view = 'workspace'
  })
  if (typeof window === 'undefined') return
  const pathname = `/ide/${encodeURIComponent(projectId)}`
  if (window.location.pathname === pathname) return
  window.history.pushState(null, '', pathname)
  window.dispatchEvent(new PopStateEvent('popstate'))
}

async function persistProjectPlan(
  projectId: string,
  studioProjectPlan: StudioProjectPlan,
  planningMetadata: StudioDecomposePlanningMetadata | undefined,
  candidateLifecycle?: StudioCandidateLifecycleDescriptor,
  librarySnapshot?: CogoXyzRegistrySnapshot,
  failureMode: 'best-effort' | 'required' = 'best-effort',
): Promise<void> {
  if (projectId.startsWith('local-')) return
  try {
    const withPlan = withStudioProjectPlanSettings(
      {},
      studioProjectPlan,
    )
    const withPlanningMetadata = planningMetadata
      ? withStudioPlanningMetadataSettings(withPlan, planningMetadata)
      : withPlan
    const withCandidateLifecycle = withCandidateLifecycleDescriptorSettings(
      withPlanningMetadata,
      candidateLifecycle,
    )
    const cogoApplicationMetadata = readRecord(
      withCandidateLifecycle.cogoApplicationMetadata,
    )
    await patchProjectSettings(projectId, {
      [STUDIO_PROJECT_PLAN_SETTINGS_KEY]: withCandidateLifecycle[
        STUDIO_PROJECT_PLAN_SETTINGS_KEY
      ] as StudioProjectPlan,
      ...(withCandidateLifecycle[STUDIO_PLANNING_METADATA_SETTINGS_KEY]
        ? {
            [STUDIO_PLANNING_METADATA_SETTINGS_KEY]:
              withCandidateLifecycle[STUDIO_PLANNING_METADATA_SETTINGS_KEY],
          }
        : {}),
      ...(Object.keys(cogoApplicationMetadata).length > 0
        ? {
            cogoApplicationMetadata,
          }
        : {}),
      ...(librarySnapshot
        ? { [COGO_XYZ_PROJECT_SETTINGS_KEY]: librarySnapshot }
        : {}),
    })
    if (librarySnapshot) {
      void pushCogoXyzRegistrySnapshotToServer(librarySnapshot)
    }
  } catch (err) {
    if (failureMode === 'required') {
      throw err instanceof Error
        ? err
        : new Error('Candidate lifecycle locator persistence failed.')
    }
    console.warn(
      '[start-app-generation] Failed to persist Studio project plan',
      err,
    )
  }
}

function syncCandidateLifecycleDescriptorInStore(
  descriptor: StudioCandidateLifecycleDescriptor,
): void {
  useStudioStore.setState((state) => {
    if (state.currentProjectId !== descriptor.projectId) return
    if (state.currentProject) {
      const project = state.currentProject as unknown as Record<string, unknown>
      project.metadata = {
        ...readRecord(project.metadata),
        candidateLifecycle: descriptor,
      }
    }
    if (state.projectMeta) {
      state.projectMeta.metadata = {
        ...readRecord(state.projectMeta.metadata),
        candidateLifecycle: descriptor,
      }
    }
  })
}

function assertCandidateLifecycleDescriptorBinding(
  descriptor: Pick<
    StudioCandidateLifecycleDescriptor,
    | 'projectId'
    | 'skuId'
    | 'candidateId'
    | 'candidateRevision'
    | 'assessmentRevision'
  >,
  expected: {
    projectId: string
    skuId: string
    candidateId: string | null
    candidateRevision: string
    assessmentRevision: string | null
  },
): void {
  if (
    descriptor.projectId !== expected.projectId ||
    descriptor.skuId !== expected.skuId ||
    (expected.candidateId !== null &&
      descriptor.candidateId !== expected.candidateId) ||
    descriptor.candidateRevision !== expected.candidateRevision ||
    descriptor.assessmentRevision !== expected.assessmentRevision
  ) {
    throw new Error('candidate_lifecycle_descriptor_binding_invalid')
  }
}

function readLifecyclePendingGenerationJob(
  projectId: string,
): AppGenerationJobDescriptor | null {
  const descriptor = readStoredAppGenerationJob(projectId)
  return descriptor?.status === 'lifecycle-pending' ? descriptor : null
}

function surfaceLifecyclePendingGenerationJob(
  descriptor: Pick<
    AppGenerationJobDescriptor,
    'projectId' | 'appSpec' | 'startedAt' | 'jobId' | 'terminalSloMs'
  >,
): void {
  const state = useStudioStore.getState()
  if (state.currentProjectId !== descriptor.projectId) return
  const frame =
    state.pageProgressFrame?.kind === 'page-progress'
      ? state.pageProgressFrame
      : null
  const pages =
    frame?.pages ??
    descriptor.appSpec.blocks.map((block) => ({
      id: block.name,
      name: block.name,
      status: 'done' as const,
    }))
  state.replacePageProgress?.({
    kind: 'page-progress',
    id: frame?.id ?? 'app-generation-progress-frame',
    timestamp: frame?.timestamp ?? Date.now(),
    titleCode: frame?.titleCode,
    pages,
    elapsedMs:
      frame?.elapsedMs ?? Math.max(0, Date.now() - descriptor.startedAt),
    evidence: frame?.evidence,
    backgroundJob: {
      schemaVersion: 'studio-app-generation-background-progress.v1',
      projectId: descriptor.projectId,
      ...(descriptor.jobId ? { jobId: descriptor.jobId } : {}),
      status: 'paused',
      stage: 'lifecycle-finalization-pending',
      canResume: true,
      continuesInBackground: true,
      ...(descriptor.terminalSloMs
        ? { terminalSloMs: descriptor.terminalSloMs }
        : {}),
    },
  })
}

function clarificationNotice(
  decision: GenerationEntrySourceSufficiencyDecision,
): string {
  if (decision.questions.length === 0) {
    return '생성 전에 최신 자료 평가가 필요합니다. Evidence에서 source assessment를 새로 확인한 뒤 다시 시도해 주세요.'
  }

  const safeLevel = decision.allowedGenerationLevel
    ? ` 현재 안전한 생성 수준은 ${decision.allowedGenerationLevel}입니다.`
    : ''
  return [
    `생성 전에 확인이 필요한 항목이 ${decision.questions.length}개 있습니다.${safeLevel}`,
    ...decision.questions.map(
      (question, index) => `${index + 1}. ${question.question}`,
    ),
    'Evidence에서 답변하면 새 assessment revision으로 다시 생성할 수 있습니다.',
  ].join('\n')
}

/**
 * Task 4 (ontology-ux-contract cycle) — chat-facing text for a
 * `generation-readiness-blocked` refusal. Mirrors `clarificationNotice`
 * immediately above it: a numbered list built from the SAME data the
 * `ReadinessPanel` renders, not a re-derived summary, so the chat notice and
 * the panel can never disagree about what is actually blocking. Only
 * `kind:'blocking'` items are listed — `unfilled`/`advisory` items never
 * stop generation, so they would only dilute the one thing this notice
 * needs to say plainly.
 */
function generationReadinessBlockedNotice(report: ReadinessReport): string {
  const blockingItems = report.items.filter((item) => item.kind === 'blocking')
  // The two refusal reasons the gate now checks independently get two
  // different opening sentences. A failed CHECK is not the same news as a
  // check that ran and found blockers, and with a status:'error' report that
  // carries no blocking item the blocker-count sentence alone would read as
  // "0 things are blocking you" — true, and completely misleading.
  const head =
    report.status === 'error'
      ? `생성 준비 점검 자체가 실패해 생성을 시작하지 않았습니다. 확인된 막는 항목은 ${blockingItems.length}개입니다.`
      : `생성 준비가 되지 않아 생성을 시작하지 않았습니다. 막는 항목이 ${blockingItems.length}개 있습니다.`
  const tail =
    report.status === 'error'
      ? '점검을 다시 시도하거나, 위 내용을 확인한 뒤 다시 생성해 주세요.'
      : '위 항목을 해결한 뒤 다시 시도해 주세요.'
  return [
    head,
    ...blockingItems.map((item, index) => {
      const suggestion = item.suggestion ? ` (${item.suggestion.label})` : ''
      return `${index + 1}. ${item.title}${suggestion}`
    }),
    tail,
  ].join('\n')
}

function rejectStudioLibraryBinding(): StudioLibraryDesignOntologyBindingError {
  return new StudioLibraryDesignOntologyBindingError(
    'studio_design_ontology_binding_rejected',
  )
}

function hasUntypedLibraryAuthority(options: StartAppGenerationOptions): boolean {
  const record = options as unknown as Record<string, unknown>
  return (
    (Object.prototype.hasOwnProperty.call(
      record,
      'libraryDesignOntologyBinding',
    ) &&
      record.libraryDesignOntologyBinding !== undefined &&
      record.libraryDesignOntologyBinding !== null) ||
    (Object.prototype.hasOwnProperty.call(record, 'libraryRagResolution') &&
      record.libraryRagResolution !== undefined &&
      record.libraryRagResolution !== null)
  )
}

/**
 * Fail closed when the active project has an imported design source (.fig)
 * but nothing supplied a host retrieval context.
 *
 * Source-bound generation is NOT implemented — see the STATUS block in
 * `source-design-system-generation-bridge.ts`. Before this guard existed the
 * combination travelled through the entire planning pipeline (decompose call,
 * tokens, project naming) and only then hit the bridge backstop with a
 * developer-only contract string, so the user paid the full planning wait for
 * a sentence they could not act on. The bridge keeps the same typed failure as
 * a backstop for any caller that bypasses this gate.
 *
 * Lanes that reject the same combination with their own, more specific message
 * are excluded: the outcome lane (server-owned spec), the pack-release lane
 * (competing design authority), and the library lane
 * (`assertStudioLibraryAuthorityIsIsolated`).
 */
function assertStudioSourceBoundGenerationSupported(input: {
  sourceWorkspace: OpenPencilSourceWorkspace | null | undefined
  options: InternalStartAppGenerationOptions
  outcomeIntent: boolean
  isLibraryGeneration: boolean
  designSystemPackRelease: boolean
  projectDesignSystemBinding: StudioProjectDesignSystemGenerationBinding | null
  store: ReturnType<typeof useStudioStore.getState>
}): void {
  if (
    !input.sourceWorkspace ||
    input.options.sourceDesignSystemGeneration ||
    input.outcomeIntent ||
    input.isLibraryGeneration ||
    input.designSystemPackRelease ||
    // R1 — the source IS followed: this project registered a design system
    // derived from the .fig now open, and `resolveProjectDesignSystemGeneration
    // Binding` already re-checked it against the live workspace. Generating is
    // no longer "silently ignoring the user's imported source", which is the
    // only thing the refusal below ever protected.
    input.projectDesignSystemBinding
  ) {
    return
  }
  const unsupported = new StudioSourceBoundGenerationUnavailableError()
  input.store.setConversation({ phase: 'gathering' })
  input.options.onGenerationError?.(unsupported.message)
  throw unsupported
}

/**
 * Resolve the imported source's registered design system for this generation.
 *
 * Returns `null` when there is no active source at all (the ordinary
 * preset/library/outcome lanes, untouched). When a source IS active, every
 * refusal is a THROW carrying wording the user can act on — never a silent
 * `null`, which would fall through to the generic "not available yet" message
 * and hide the one instruction that actually unblocks them.
 */
function resolveStudioProjectDesignSystemBinding(input: {
  sourceWorkspace: OpenPencilSourceWorkspace | null | undefined
  sourceProjectId: string | null | undefined
  options: InternalStartAppGenerationOptions
  outcomeIntent: boolean
  isLibraryGeneration: boolean
  designSystemPackRelease: boolean
  store: ReturnType<typeof useStudioStore.getState>
}): StudioProjectDesignSystemGenerationBinding | null {
  if (
    !input.sourceWorkspace ||
    input.options.sourceDesignSystemGeneration ||
    input.outcomeIntent ||
    input.isLibraryGeneration ||
    input.designSystemPackRelease
  ) {
    return null
  }
  const registration = input.store.projectDesignSystemRegistration
  const fail = (code: ProjectDesignSystemGenerationBindingErrorCode): never => {
    const message = describeProjectDesignSystemGenerationBlocker(code)
    input.store.setConversation({ phase: 'gathering' })
    input.options.onGenerationError?.(message)
    throw new StudioSourceBoundGenerationUnavailableError(message)
  }
  if (!registration) fail('project_design_system_not_registered')
  try {
    const binding = resolveProjectDesignSystemGenerationBinding({
      registration,
      workspace: input.sourceWorkspace,
      sourceProjectId: input.sourceProjectId,
    })
    if (!binding) fail('project_design_system_not_registered')
    return binding
  } catch (cause) {
    if (cause instanceof ProjectDesignSystemGenerationBindingError) {
      fail(cause.code)
    }
    throw cause
  }
}

function assertStudioLibraryAuthorityIsIsolated(
  options: InternalStartAppGenerationOptions,
): void {
  const store = useStudioStore.getState()
  const effectiveDesignSystemPreset =
    options.designSystemPreset === undefined
      ? store.conversation.designSystemPreset
      : options.designSystemPreset
  if (
    effectiveDesignSystemPreset ||
    options.sourceDesignSystemGeneration ||
    store.openPencilSourceWorkspace
  ) {
    throw rejectStudioLibraryBinding()
  }
}

/**
 * Server-owned persistence — the generation workflow can now write this
 * project's `studio_blocks` rows mid-generation, before the request as a
 * whole is reported success or failure. The failure-cleanup path
 * (`cleanupCreatedProject` below) therefore cannot assume an unsuccessful
 * request left nothing behind: it must ask the server how many blocks this
 * project actually holds before ever deleting it.
 *
 * This reuses the SAME authenticated project-load call the editor uses to
 * open a project (`loadProject` → `/api/projects/:id/full`) — its
 * `blockIdMap` is built directly from the returned `studio_blocks` rows, so
 * its key count is exactly the persisted-block count. No new endpoint, and
 * no extra round trip on the happy path: this only ever runs from the
 * failure/cleanup path.
 *
 * W0-2 D5 — threads the generation's `traceId` so this cleanup read joins the
 * same create→persist→readback correlation in the Worker's access log instead
 * of appearing as an unattributed project load.
 */
async function countPersistedBlocks(
  projectId: string,
  traceId?: string,
): Promise<number> {
  const { blockIdMap } = await loadProject(projectId, traceId)
  return Object.keys(blockIdMap).length
}

function fingerprintGenerationPrompt(prompt: string): string {
  const seeds = [
    0x811c9dc5, 0x9e3779b9, 0x85ebca6b, 0xc2b2ae35, 0x27d4eb2f, 0x165667b1,
    0xd3a2646c, 0xfd7046c5,
  ]
  const lanes = seeds.map((seed, lane) => {
    let hash = seed >>> 0
    for (let index = 0; index < prompt.length; index += 1) {
      hash =
        Math.imul(
          hash ^ (prompt.charCodeAt(index) + lane * 0x9e37),
          0x01000193,
        ) >>> 0
    }
    return hash.toString(16).padStart(8, '0')
  })
  return `prompt:fingerprint-v1:${lanes.join('')}`
}

function sourceDerivedAxes(
  designSystem: ReadinessReport['designSystem'],
): Pick<
  StudioGenerationAuthorityEnvelope['designAuthority'],
  'lockedAxes' | 'unresolvedAxes'
> {
  if (!designSystem)
    throw new Error(
      'Source-derived generation authority requires readiness design-system facts.',
    )
  const lockedAxes: AxisName[] = []
  const unresolvedAxes: AxisName[] = []
  for (const axis of STUDIO_GENERATION_DESIGN_AXES) {
    if (designSystem.axes[axis].origin === 'gap') unresolvedAxes.push(axis)
    else lockedAxes.push(axis)
  }
  return { lockedAxes, unresolvedAxes }
}

function generationDesignAuthority(input: {
  options: InternalStartAppGenerationOptions
  requestedDesignSystemPreset: DesignSystemState | null | undefined
  creatorDefaultDesignSystem: CreatorDefaultDesignSystemBinding | null
  projectDesignSystemBinding: StudioProjectDesignSystemGenerationBinding | null
  designSystemPackRelease: StudioDesignSystemPackReleaseBinding | undefined
  readiness: ReadinessReport
  outcomeIntent: StudioOutcomeIntent | undefined
  isLibraryGeneration: boolean
}): StudioGenerationAuthorityEnvelope['designAuthority'] {
  if (input.designSystemPackRelease) {
    const release = input.designSystemPackRelease
    return {
      mode: 'pack-release-exact',
      authorityRef: `design-pack:${release.releaseId}`,
      evidenceRefs: [release.releaseContentHash, release.packageHash],
      lockedAxes: [...STUDIO_GENERATION_DESIGN_AXES],
      unresolvedAxes: [],
    }
  }
  if (input.projectDesignSystemBinding) {
    const lineage = input.projectDesignSystemBinding.lineage
    return {
      mode: 'source-derived',
      authorityRef: `source-design:${lineage.registrationContentHash}`,
      evidenceRefs: [lineage.registrationContentHash, lineage.fileSha256],
      ...sourceDerivedAxes(input.readiness.designSystem),
    }
  }
  if (input.options.sourceDesignSystemGeneration) {
    return {
      mode: 'source-derived',
      authorityRef: 'source-design:studio-generation-context',
      evidenceRefs: ['source-design:studio-generation-context'],
      ...sourceDerivedAxes(input.readiness.designSystem),
    }
  }
  if (input.isLibraryGeneration) {
    const releaseRef = input.options.libraryRagResolution
      ? input.options.libraryRagResolution.artifact.designOntologyReleaseRef
      : input.options.libraryDesignOntologyBinding!.application
          .designOntologyReleaseRef
    return {
      mode: 'user-selected',
      authorityRef: `design-ontology:${releaseRef}`,
      evidenceRefs: [`design-ontology:${releaseRef}`],
      lockedAxes: [...STUDIO_GENERATION_DESIGN_AXES],
      unresolvedAxes: [],
    }
  }
  if (input.outcomeIntent) {
    const authorityRef = `studio-outcome:${input.outcomeIntent.skuId}`
    return {
      mode: 'user-selected',
      authorityRef,
      evidenceRefs: [authorityRef],
      lockedAxes: [...STUDIO_GENERATION_DESIGN_AXES],
      unresolvedAxes: [],
    }
  }
  if (input.requestedDesignSystemPreset) {
    const presetRef = slugDesignSystemId(input.requestedDesignSystemPreset)
    return {
      mode: 'user-selected',
      authorityRef: `design-preset:${presetRef}`,
      evidenceRefs: [`design-preset:${presetRef}`],
      lockedAxes: [...STUDIO_GENERATION_DESIGN_AXES],
      unresolvedAxes: [],
    }
  }
  const creatorRef = input.creatorDefaultDesignSystem?.presetId
    ? `design-preset:${input.creatorDefaultDesignSystem.presetId}`
    : 'design-preset:creator-default'
  return {
    mode: 'creator-default',
    authorityRef: creatorRef,
    evidenceRefs: [creatorRef],
    lockedAxes: [],
    unresolvedAxes: [],
  }
}

async function startAppGenerationFromPromptWithLease(
  prompt: string,
  options: InternalStartAppGenerationOptions = {},
): Promise<StartAppGenerationOutcome> {
  const isLibraryGeneration = Boolean(
    options.libraryDesignOntologyBinding || options.libraryRagResolution,
  )
  if (
    options.libraryDesignOntologyBinding &&
    options.libraryRagResolution
  ) {
    throw rejectStudioLibraryBinding()
  }
  if (isLibraryGeneration) {
    assertStudioLibraryAuthorityIsIsolated(options)
  }
  // 2026-08-17 wave-4 lane-2 -- `options.trigger`, when present, MUST be
  // genuine classifier-dispatch provenance (studio-creation-router.ts) --
  // fails closed with a distinct `GenerationTriggerProvenanceError` code
  // before any planning/network work, same as the other synchronous
  // fail-closed checks in this block. It is optional here (not required for
  // every caller of this internal function) because this single choke point
  // is shared by callers this lane did not touch and must not regress: the
  // wizard (`studioProjectPlan`), the dev pack-release trigger
  // (`designSystemPackRelease`, itself hash-verified upstream), cogo-xyz's
  // source import, and the library entry points above all reach here with
  // their own, separately-verified authority and no trigger claim at all --
  // exactly as before this fix. `startAppGenerationFromClassifiedPrompt`
  // (exported below) makes the trigger REQUIRED for the chat/classifier
  // surface (hero-chat-input.tsx, studio-composer.tsx, and the shared
  // generation-readiness-confirmed path) that wave-3B's TODO was about.
  if (options.trigger !== undefined) {
    assertGenuineGenerationTrigger(options.trigger)
  }
  throwIfGenerationAborted(options.signal)
  const store = useStudioStore.getState()
  const sourceWorkspace = store.openPencilSourceWorkspace
  const sourceWorkspaceProjectId = store.currentProjectId
  // G2 — `requestedDesignSystemPreset` is the USER-signal value (options or
  // conversation) and is what conversation state keeps carrying;
  // `designSystemPreset` is the effective generation-context value, which
  // the creator default resolution below may upgrade from null.
  const requestedDesignSystemPreset =
    options.designSystemPreset === undefined
      ? store.conversation.designSystemPreset
      : options.designSystemPreset
  let designSystemPreset = requestedDesignSystemPreset
  const sourceProject =
    options.sourceProject === undefined
      ? store.currentProject
      : options.sourceProject
  const outcomeIntent = options.outcomeIntent
    ? parseStudioOutcomeIntent(options.outcomeIntent)
    : undefined
  if (
    options.applicationAuthority?.mode === 'locked-exact-spec' &&
    !options.exactGenerationAuthority
  ) {
    throw new Error(
      'Locked exact application authority must be supplied through exactGenerationAuthority.',
    )
  }
  if (options.exactGenerationAuthority) {
    const competing = options.applicationAuthority
      ? 'application-authority'
      : options.designSystemPackRelease
        ? 'design-system-pack-release'
        : requestedDesignSystemPreset
          ? 'design-system-preset'
          : sourceWorkspace
            ? 'source-workspace'
            : options.sourceDesignSystemGeneration
              ? 'source-design-system-generation'
              : isLibraryGeneration
                ? 'library-design-binding'
                : outcomeIntent
                  ? 'outcome-intent'
                  : options.sourceProject !== undefined
                    ? 'source-project'
                    : options.studioProjectPlan
                      ? 'studio-project-plan'
                      : options.candidateLifecycle
                        ? 'candidate-lifecycle'
                        : null
    if (competing) {
      throw new Error(
        `studio_exact_generation_authority_isolation_rejected:${competing}`,
      )
    }
  }
  const resolvedExactGenerationAuthority = options.exactGenerationAuthority
    ? resolveProductionStudioExactGenerationAuthority(
        options.exactGenerationAuthority,
      )
    : undefined
  const providedApplicationAuthority =
    resolvedExactGenerationAuthority?.application ??
    options.applicationAuthority
  if (
    providedApplicationAuthority?.mode === 'provided-spec' &&
    providedApplicationAuthority.appSpecRef !==
      fingerprintAppGenerationSpec(providedApplicationAuthority.appSpec)
  ) {
    throw new Error(
      'Provided application spec reference conflicts with its fingerprint.',
    )
  }
  // F5 — a pack release binding is the SOLE design authority when present.
  // Reject every competing authority up front (fail-closed): silently
  // combining two design identities would make the persisted lineage a lie.
  const designSystemPackRelease =
    resolvedExactGenerationAuthority?.designSystemPackRelease ??
    options.designSystemPackRelease
  if (designSystemPackRelease && !resolvedExactGenerationAuthority) {
    const competing = isLibraryGeneration
      ? 'library-design-binding'
      : outcomeIntent
        ? 'outcome-intent'
        : sourceWorkspace
          ? 'source-workspace'
          : options.sourceDesignSystemGeneration
            ? 'source-design-system-generation'
            : requestedDesignSystemPreset
              ? 'design-system-preset'
              : options.studioProjectPlan
                ? 'studio-project-plan'
                : options.candidateLifecycle
                  ? 'candidate-lifecycle'
                  : null
    if (competing) {
      throw new Error(
        `studio_design_system_pack_release_isolation_rejected:${competing}`,
      )
    }
  }
  const sourceSufficiency =
    evaluateGenerationEntrySourceSufficiency(sourceProject)

  if (options.candidateLifecycle && outcomeIntent) {
    throw new Error(
      'Candidate lifecycle input and Studio outcome intent are mutually exclusive.',
    )
  }

  if (sourceSufficiency.action === 'ask-first') {
    store.setConversation({
      confirmedNarrative: prompt,
      confirmedGoals: [prompt],
      designSystemPreset: requestedDesignSystemPreset,
      phase: 'gathering',
    })
    store.setPendingChatNotice(clarificationNotice(sourceSufficiency))
    options.onClarificationRequired?.(sourceSufficiency)
    return {
      status: 'clarification-required',
      sourceSufficiency,
    }
  }

  // G2 — mandatory design-system binding (design doc §2.3, RC-B). The plain
  // creator path (no library binding, no OpenPencil source authority, no
  // server-owned outcome) must never generate unbound: resolve the catalog
  // default preset (and, when possible, a real design_system_presets row id
  // for studio_projects.design_system_preset_id) before planning, so the
  // decompose call and the generated blocks receive real design tokens.
  // Source-bound generation is not implemented (see the STATUS block in
  // source-design-system-generation-bridge.ts). An active imported source used
  // to travel all the way through planning — decompose call, tokens, project
  // naming — and only then hit the bridge backstop with a developer-only
  // contract string, so the user paid the full planning wait to receive a
  // sentence they could not act on. Fail closed HERE, before any planning
  // work, with wording an end user can act on.
  //
  // Lanes with their own, more specific message keep it: the outcome lane
  // rejects the same combination explicitly further down, the pack-release
  // lane rejected it above as a competing authority, and the library lane
  // rejects it in `assertStudioLibraryAuthorityIsIsolated`.
  // R1 — resolve the imported source's registered design system BEFORE the
  // support gate, so the gate can tell "source ignored" (still refused) apart
  // from "source followed" (now generates). Both live before any planning work
  // for the same reason the gate does: a refusal must not cost a full planning
  // wait.
  const projectDesignSystemBinding = resolveStudioProjectDesignSystemBinding({
    sourceWorkspace,
    sourceProjectId: sourceWorkspaceProjectId,
    options,
    outcomeIntent: Boolean(outcomeIntent),
    isLibraryGeneration,
    designSystemPackRelease: Boolean(designSystemPackRelease),
    store,
  })
  assertStudioSourceBoundGenerationSupported({
    sourceWorkspace,
    options,
    outcomeIntent: Boolean(outcomeIntent),
    isLibraryGeneration,
    designSystemPackRelease: Boolean(designSystemPackRelease),
    projectDesignSystemBinding,
    store,
  })

  // Task 4 (ontology-ux-contract cycle) — the ONE pre-generation readiness
  // gate, shared by all three exported entry points (they all fall through
  // to this same function). Deliberately placed AFTER the two synchronous
  // fail-closed checks just above (both are cheap, in-memory, no I/O — not
  // "planning work" in the sense the surrounding comments mean) so their
  // own, more specific end-user wording keeps firing for the scenarios they
  // already own (e.g. an imported source with no host-provided retrieval
  // context) rather than being shadowed by this gate's more generic
  // "readiness check failed" message. It still runs before ANY real
  // planning/project-creation work — the same fail-closed-before-planning
  // rationale as the sourceSufficiency gate above and the R1 gate this one
  // now follows: a refusal must never cost a full planning wait.
  //
  // Today's production wiring (register-studio-tools.ts's
  // registerGenerationReadinessTools call) can only ever produce
  // blockingCount:0 on the happy path — design-system axes surface as
  // 'unfilled', never 'blocking', and collectMachineGaps reports
  // {performed:false} because no pre-generation candidate exists yet to run
  // library/ontology admission against (see check-readiness.ts /
  // register-readiness-tools.ts). This mirrors that same honest stub rather
  // than inventing a different wiring for this one call site. This gate is
  // NOT dead code, though: checkGenerationReadiness folds a THROWING
  // loadTokens into status:'error' with one blocking item, and
  // loadCurrentProjectSourceTokens itself throws by design when a project
  // has a registered source that fails to load right now (capability.ts's
  // own fail-closed contract: "this tool will not silently derive from
  // nothing"). So this guard is genuinely reachable in production today,
  // not only via test injection: a project whose registered design source
  // cannot be read right now must not generate pretending the source
  // doesn't exist.
  // Task 5.5 — this call site used to carry its own inline copy of the
  // production wiring (loadTokens + the performed:false honesty stub); it
  // now consumes the ONE shared factory (studio-readiness-check.ts) that the
  // WebMCP registration and the chat presentation layer also read, so the
  // three surfaces can never disagree about what the judgment is.
  const readinessReport = await checkStudioGenerationReadiness(
    sourceWorkspaceProjectId ?? 'unsaved',
  )
  // `status === 'error'` is checked INDEPENDENTLY of `blockingCount`, exactly
  // as `readiness-panel.tsx` does for the generate control. Today the two are
  // equivalent by coincidence — `errorReport` (check-readiness.ts) always
  // packs one blocking item alongside the error status — but this gate must
  // not inherit that as an assumption. If an upstream change ever produced an
  // error report with no blocking item, a `blockingCount`-only guard would
  // let a FAILED check start generation: the exact "check failed, therefore
  // proceed" fallback the spec forbids (§오류 처리: "점검 호출 자체 실패 →
  // error 상태 + retry. '생성 가능' 폴백 금지"). Two independent reasons to
  // refuse, so neither depends on the other holding.
  if (readinessReport.status === 'error' || readinessReport.blockingCount > 0) {
    store.setPendingChatNotice(generationReadinessBlockedNotice(readinessReport))
    return {
      status: 'generation-readiness-blocked',
      readiness: readinessReport,
    }
  }

  let creatorDefaultDesignSystem: CreatorDefaultDesignSystemBinding | null =
    null
  if (
    !isLibraryGeneration &&
    !designSystemPreset &&
    !sourceWorkspace &&
    !options.sourceDesignSystemGeneration &&
    !outcomeIntent &&
    // F5 — a verified pack release already owns the design system; the G2
    // creator default must not compete with (or shadow) it.
    !designSystemPackRelease
  ) {
    creatorDefaultDesignSystem =
      await resolveCreatorDefaultDesignSystemBinding(store.user?.id ?? null)
    designSystemPreset = creatorDefaultDesignSystem.preset
  }
  try {
    // Fail-closed backstop: a non-library request with no design system (and
    // no other design authority) must fail visibly, never proceed unbound.
    assertCreatorGenerationDesignSystemAuthority({
      designSystemPreset,
      hasLibraryDesignAuthority: isLibraryGeneration,
      hasSourceDesignAuthority: Boolean(
        sourceWorkspace || options.sourceDesignSystemGeneration,
      ),
      hasServerOwnedOutcomeAuthority: Boolean(outcomeIntent),
      hasPackReleaseAuthority: Boolean(designSystemPackRelease),
    })
  } catch (cause) {
    const message =
      cause instanceof Error
        ? cause.message
        : 'creator_generation_design_system_missing'
    store.setConversation({ phase: 'gathering' })
    options.onGenerationError?.(message)
    throw cause instanceof Error ? cause : new Error(message)
  }

  const isChatGeneration =
    !isLibraryGeneration &&
    (options.generationEntry ?? 'chat') === 'chat'
  let chatDraftPreparation:
    | ChatGeneratedApplicationDraftPreparationReceipt
    | null = null
  let chatDraftAttempt:
    | ReturnType<typeof chatGenerationDraftAttemptForRequestContent>
    | null = null
  if (isChatGeneration) {
    const accessToken = await getStudioAccessToken()
    throwIfGenerationAborted(options.signal)
    const attempt = chatGenerationDraftAttemptForRequestContent(
      prompt,
      options.chatGenerationAttemptScope ?? store.user?.id,
    )
    chatDraftAttempt = attempt
    chatDraftPreparation = await prepareChatGeneratedApplicationDraft({
      accessToken: accessToken ?? '',
      idempotencyKey: attempt.idempotencyKey,
      requestContent: prompt,
      signal: options.signal,
    })
    throwIfGenerationAborted(options.signal)
  }

  const authoritySourceSufficiency =
    sourceWorkspace && sourceWorkspaceProjectId
      ? {
          ...sourceSufficiency,
          evidenceRefs: [
            ...sourceSufficiency.evidenceRefs,
            `source-project:${sourceWorkspaceProjectId}`,
          ],
        }
      : sourceSufficiency
  const rightsReceiptRefs = resolvedExactGenerationAuthority
    ? [...resolvedExactGenerationAuthority.rightsEvidenceRefs]
    : projectDesignSystemBinding
      ? [projectDesignSystemBinding.lineage.registrationContentHash]
      : ['rights:studio-generation-preview-only']
  if (chatDraftPreparation) {
    rightsReceiptRefs.push(
      chatDraftPreparation.requestEnvelopeRef.ref,
      chatDraftPreparation.receiptRef.ref,
    )
  }
  const commonGenerationEntryFacts = normalizeStudioGenerationEntryFacts({
    entry: options.generationEntry ?? 'chat',
    promptRef: fingerprintGenerationPrompt(prompt),
    readiness: readinessReport,
    ...(options.applicationAuthority
      ? { applicationAuthority: options.applicationAuthority }
      : {}),
    ...(resolvedExactGenerationAuthority
      ? { exactGenerationAuthority: resolvedExactGenerationAuthority }
      : {}),
  })
  const generationAuthorityInput = buildStudioGenerationAuthorityInput({
    commonFacts: commonGenerationEntryFacts,
    // Chat has a server-reserved destination already. Other entry points keep
    // the historical unbound preflight and bind the project at creation.
    // Source workspace identity belongs in sourceSufficiency evidence; it is
    // never substituted for the destination project's authority.
    projectId: chatDraftPreparation?.subject.projectId ?? null,
    tenantId: chatDraftPreparation?.subject.tenantId ?? null,
    sourceSufficiency: authoritySourceSufficiency,
    designAuthority: generationDesignAuthority({
      options,
      requestedDesignSystemPreset,
      creatorDefaultDesignSystem,
      projectDesignSystemBinding,
      designSystemPackRelease,
      readiness: readinessReport,
      outcomeIntent,
      isLibraryGeneration,
    }),
    rights: {
      previewAllowed: true,
      sameProjectPublicationAllowed: false,
      packPromotionAllowed: false,
      receiptRefs: [...rightsReceiptRefs],
    },
    questions: authoritySourceSufficiency.questions,
    claimCeiling: {
      maxGenerationLevel: authoritySourceSufficiency.allowedGenerationLevel,
      exactVisualConformanceRequired: Boolean(resolvedExactGenerationAuthority),
      sourceReadbackRequired: Boolean(
        projectDesignSystemBinding,
      ),
    },
  })
  const generationAuthorityPreflight =
    buildStudioGenerationAuthorityPreflight(generationAuthorityInput)
  if (providedApplicationAuthority?.mode === 'partial-provided-spec') {
    validatePartialProvidedApplicationAuthority(providedApplicationAuthority)
  }

  store.setConversation({
    confirmedNarrative: prompt,
    confirmedGoals: [prompt],
    // The auto-resolved default is generation-context authority, not a user
    // selection: conversation keeps the user-signal value so the library
    // lane's authority-isolation check (assertStudioLibraryAuthorityIsIsolated
    // reads conversation.designSystemPreset) is not poisoned by it.
    designSystemPreset: requestedDesignSystemPreset,
    phase: 'generating',
  })
  // The dashboard already owns and executes this generation request. Do not
  // auto-append it into the mounted assistant runtime: doing so submits the
  // same prompt a second time through Chat/WebMCP and can produce a response
  // that contradicts the authoritative generation result. The confirmed
  // narrative and the pending notice keep the request visible without a
  // second model invocation.

  // App creation is fail-closed. Local heuristic plans remain useful in unit
  // tests and design tools, but they are not ontology authority and must never
  // be promoted into a user project.
  let fallbackReason: string | undefined
  const forwardPlanningProgress = (progress: DecomposePlanningProgress) => {
    if (progress.phase === 'fallback' || progress.phase === 'timeout') {
      fallbackReason = progress.message
    }
    options.onPlanningProgress?.(progress)
  }
  let plan: PromptAppPlan
  let ontologyEvidencePlan: PromptAppPlan | null = null
  let planningMetadata: StudioDecomposePlanningMetadata | undefined
  if (
    providedApplicationAuthority?.mode === 'provided-spec' ||
    providedApplicationAuthority?.mode === 'locked-exact-spec'
  ) {
    plan = promptAppPlanFromProvidedAppSpec(
      providedApplicationAuthority.appSpec,
    )
  } else {
    const livePlan = await buildAppPlanFromDecomposeApi(prompt, {
      designSystemPreset,
      ...(isLibraryGeneration
        ? { designOntologyPlanningMode: 'semantic-only' as const }
        : {}),
      signal: options.signal,
      timeoutMs: options.planningTimeoutMs,
      onPlanningProgress: forwardPlanningProgress,
      ...(chatDraftPreparation
        ? { chatGeneratedApplicationDraft: chatDraftPreparation }
        : {}),
      // The five public fields, unchanged, through the SAME planner call and
      // the SAME `/api/decompose` request every ordinary generation uses.
      ...(options.applicationOntologyBootstrap
        ? { applicationOntologyBootstrap: options.applicationOntologyBootstrap }
        : {}),
    })
    throwIfGenerationAborted(options.signal)
    if (
      !livePlan?.planningMetadata ||
      livePlan.planningMetadata.status !== 'live'
    ) {
      const message = fallbackReason ?? livePlannerFailureMessage('unavailable')
      store.setConversation({ phase: 'gathering' })
      options.onGenerationError?.(message)
      throw new Error(message)
    }
    planningMetadata = livePlan.planningMetadata
    // F5 — the verified pack tokens become the plan's presentation authority
    // through the SAME appSpec fields the G2 preset path populates
    // (themeHint/designSystemTokens), so the downstream requestAppGeneration
    // token wiring and project.tokens injection follow without new seams.
    // R1 — a registered source design system is this generation's presentation
    // authority through the same fields, plus `appSpec.designSystem`, which is
    // the only token channel the generator itself reads (see
    // project-design-system-generation-binding.ts). The two bindings are
    // mutually exclusive upstream: a pack release rejects an active source
    // workspace as a competing authority before planning starts.
    const plannerPlan = designSystemPackRelease
      ? withDesignSystemPackReleaseGenerationAuthority(
          livePlan,
          designSystemPackRelease,
        )
      : projectDesignSystemBinding
        ? withProjectDesignSystemGenerationAuthority(
            livePlan,
            projectDesignSystemBinding,
          )
        : livePlan
    ontologyEvidencePlan = plannerPlan
    if (providedApplicationAuthority?.mode === 'partial-provided-spec') {
      const mergedPlan = promptAppPlanFromProvidedAppSpec(
        mergePlannerCandidateIntoOpenSlots({
          authority: providedApplicationAuthority,
          candidate: plannerPlan.appSpec,
        }),
      )
      plan = {
        ...plannerPlan,
        ...mergedPlan,
        planningMetadata,
      }
    } else {
      plan = plannerPlan
    }
  }
  if (
    options.studioProjectPlan?.designSystemRelease &&
    plan.appSpec.projectPlan?.designSystemRelease &&
    !preservesSelectedDesignSystemRelease(
      options.studioProjectPlan,
      plan.appSpec.projectPlan,
    )
  ) {
    const message =
      'Approved design-system generation is blocked: the live planner returned a conflicting immutable release identity.'
    store.setConversation({ phase: 'gathering' })
    options.onGenerationError?.(message)
    throw new Error(message)
  }
  if (outcomeIntent) {
    try {
      assertStudioOutcomeGenerationAuthority(plan, outcomeIntent.skuId)
    } catch (cause) {
      const message =
        cause instanceof Error
          ? cause.message
          : 'studio_outcome_generation_authority_mismatch'
      store.setConversation({ phase: 'gathering' })
      options.onGenerationError?.(message)
      throw cause instanceof Error ? cause : new Error(message)
    }
  }
  const ontologyPlan = ontologyEvidencePlan ?? plan
  const ontologyQuality =
    !planningMetadata ||
    (providedApplicationAuthority &&
      providedApplicationAuthority.mode !== 'partial-provided-spec')
      ? null
      : evaluateChatOntologyGenerationQuality({
          prompt,
          planningSource: planningMetadata.planningSource,
          decomposeElapsedMs: planningMetadata.elapsedMs,
          decomposeTotalMs: planningMetadata.totalMs,
          decomposeUpstreamMs: planningMetadata.upstreamMs,
          ontologyTrace: ontologyPlan.appSpec.ontologyRetrievalTrace,
          expectedBlockNames: ontologyPlan.blocks.map((block) => block.name),
          generatedBlockNames: ontologyPlan.blocks.map((block) => block.name),
          persistedBlockNames: ontologyPlan.blocks.map((block) => block.name),
          actionFlowCompilerValid:
            ontologyPlan.appSpec.actionFlowCompilerProof?.valid,
          actionFlowRuntimeValid:
            ontologyPlan.appSpec.actionFlowRuntimeSimulationProof?.valid,
          actionFlowReadiness: ontologyPlan.appSpec.actionFlowReadiness,
        })
  const ontologyErrors = (ontologyQuality?.issues ?? []).filter(
    (issue) => issue.severity === 'error',
  )
  if (ontologyErrors.length > 0) {
    const message = `Ontology admission blocked app generation: ${ontologyErrors
      .map((issue) => issue.message)
      .join(' ')}`
    // The codes travel STRUCTURED alongside the prose. They are the producer's
    // own vocabulary — passed through, never re-derived here — so a new code
    // reaches the creator surface without this line changing.
    const ontologyIssueCodes = ontologyErrors.map((issue) => issue.code)
    store.setConversation({ phase: 'gathering' })
    options.onGenerationError?.(message, { ontologyIssueCodes })
    throw Object.assign(new Error(message), { ontologyIssueCodes })
  }
  // G3 — U2 library-lookup judgment moment (design doc §2.1): AFTER planning
  // succeeds and BEFORE the server project exists, surface admitted library
  // structure candidates for the PLAIN creator path only. Library, source,
  // outcome and wizard authorities keep their own contracts untouched, and
  // this never writes conversation.designSystemPreset (the library lane's
  // authority-isolation invariant). Every non-selection outcome proceeds
  // byte-identically to the pre-G3 path.
  let libraryStructureSelection: LibraryStructureSelection | undefined
  if (
    options.onLibraryStructureSelection &&
    !isLibraryGeneration &&
    !outcomeIntent &&
    !options.studioProjectPlan &&
    !sourceWorkspace &&
    !options.sourceDesignSystemGeneration &&
    // F5 — a pack-designated generation is an explicit build order, not a
    // browse moment; the U2 judgment cards stay out of its way.
    !designSystemPackRelease
  ) {
    libraryStructureSelection = await resolveLibraryStructureSelection(
      prompt,
      options.onLibraryStructureSelection,
      options.signal,
    )
    throwIfGenerationAborted(options.signal)
  }
  // F5 — the pack packageId is the DECLARED design identity carried on the
  // project plan (settings.studioProjectPlan.designSystemId), alongside the
  // full lineage persisted at birth under settings.designSystemPackRelease.
  const designSystemId = designSystemPackRelease
    ? designSystemPackRelease.packageId
    : designSystemPreset
      ? slugDesignSystemId(designSystemPreset)
      : undefined

  let studioProjectPlan = outcomeIntent
    ? plan.appSpec.projectPlan!
    : (options.studioProjectPlan ??
      plan.appSpec.projectPlan ??
      deriveStudioProjectPlanFromGeneration({
        prompt,
        name: plan.appSpec.name,
        appClass: plan.appSpec.appClass,
        description: plan.appSpec.description,
        runtimeTargets: plan.appSpec.runtimeTargets,
        targetPlatform: plan.appSpec.targetPlatform,
        generationTargetPlatform: plan.appSpec.generationTargetPlatform,
        interactionContract: plan.appSpec.interactionContract,
        datasources: plan.appSpec.datasources,
        globalState: plan.appSpec.globalState,
        designSystemId,
        designSystemPresetId: creatorDefaultDesignSystem?.presetId,
      }))
  // The planner may return an already-authored projectPlan. Its canonical
  // designSystemId remains semantic authority; attach the independently
  // resolved database preset row binding after plan selection so that branch
  // cannot bypass the mapping persisted with the project birth record.
  if (creatorDefaultDesignSystem?.presetId) {
    studioProjectPlan = {
      ...studioProjectPlan,
      designSystemPresetId: creatorDefaultDesignSystem.presetId,
    }
  }
  let libraryDesignBinding:
    | Awaited<ReturnType<typeof bindStudioLibraryDesignOntology>>
    | ReturnType<typeof bindStudioLibraryRagResolution>
    | undefined
  if (isLibraryGeneration) {
    try {
      assertStudioLibraryAuthorityIsIsolated(options)
      if (sourceWorkspace) {
        throw rejectStudioLibraryBinding()
      }
      const binding = options.libraryRagResolution
        ? bindStudioLibraryRagResolution({
            projectPlan: studioProjectPlan,
            resolution: options.libraryRagResolution,
          })
        : await bindStudioLibraryDesignOntology({
            prompt,
            projectPlan: studioProjectPlan,
            binding: options.libraryDesignOntologyBinding!,
          })
      assertStudioLibraryAuthorityIsIsolated(options)
      studioProjectPlan = binding.projectPlan
      libraryDesignBinding = binding
    } catch (cause) {
      const message =
        cause instanceof Error
          ? cause.message
          : 'studio_design_ontology_binding_rejected'
      store.setConversation({ phase: 'gathering' })
      options.onGenerationError?.(message)
      throw cause instanceof Error ? cause : new Error(message)
    }
  }
  let appSpec: AppSpecFull
  if (providedApplicationAuthority) {
    // Complete, partial, and exact authorities already passed the canonical
    // application responsibility/schema boundary in their conversion seam.
    // Re-running the planner post-processors here would author fields outside
    // declared open slots and could mutate a locked path.
    appSpec = plan.appSpec
  } else if (outcomeIntent) {
    // The selected SKU and every dependent proof are server-owned. The
    // boundary above has admitted the exact response, so do not regenerate or
    // splice browser-authored authority into this AppSpec. The source-lineage
    // gate therefore rejects the combination outright instead of splicing a
    // promoted design-system contract into a server-owned spec.
    if (options.sourceDesignSystemGeneration || sourceWorkspace) {
      throw new Error(
        'Studio outcome generation is server-owned; it cannot run with an active OpenPencil source workspace or a source design-system generation context.',
      )
    }
    appSpec = plan.appSpec
  } else if (libraryDesignBinding) {
    if (!planningMetadata) {
      throw new Error(
        'Library generation requires live semantic planner metadata.',
      )
    }
    appSpec = projectStudioLibraryDesignBoundAppSpec({
      appSpec: withProjectPlanMetadata(
        plan.appSpec,
        studioProjectPlan,
        planningMetadata,
      ),
      projectPlan: studioProjectPlan,
      artifact: libraryDesignBinding.artifact,
      ...('evidence' in libraryDesignBinding
        ? { libraryRagOrchestrationEvidence: libraryDesignBinding.evidence }
        : {}),
    })
  } else {
    if (!planningMetadata) {
      throw new Error('Studio generation requires live semantic planner metadata.')
    }
    const appSpecWithOntology = withStudioOntologyRetrievalTrace(
      withStudioReportApplicationIntegration(
        withProjectPlanMetadata(
          plan.appSpec,
          studioProjectPlan,
          planningMetadata,
        ),
        { prompt, source: 'studio-start-app-generation' },
      ),
      { prompt, source: 'studio-start-app-generation', preserveExistingSource: true },
    )
    const appSpecWithEvidence = withImageEvidenceVerificationShadow(
      withApplicationResponsibilityDecisions(
        withActionFlowRuntimeSimulationProof(appSpecWithOntology, {
          source: 'studio-start-app-generation',
        }),
        { source: 'studio-start-app-generation', preserveExistingSource: true },
      ),
      {
        source: 'studio-start-app-generation',
        enabled: options.imageEvidenceVerificationEnabled,
        imageSourceEvidence: options.imageSourceEvidence,
        imageEvidenceSource: options.imageEvidenceSource,
        pixelRagVisualSegments: options.pixelRagVisualSegments,
        inferredHiddenTruths: options.inferredHiddenTruths,
      },
    )
    const templateBound = Boolean(
      appSpecWithEvidence.templateEnforcement ||
      appSpecWithEvidence.templateGenerationModelPolicy,
    )
    const appSpecWithTemplatePolicy: AppSpecFull = {
      ...appSpecWithEvidence,
      ...(templateBound
        ? {
            templateGenerationModelPolicy: buildTemplateGenerationModelPolicy({
              templateEnforcement: appSpecWithEvidence.templateEnforcement,
              suppliedPolicy: appSpecWithEvidence.templateGenerationModelPolicy,
            }),
          }
        : {}),
    }
    appSpec = withStudioSourceDesignSystemGenerationContract({
      appSpec: appSpecWithTemplatePolicy,
      workspace: sourceWorkspace,
      context: options.sourceDesignSystemGeneration,
      // R1 evidence — raw, so the backstop recomputes the verdict instead of
      // trusting this call site's `projectDesignSystemBinding`.
      projectDesignSystemRegistration: store.projectDesignSystemRegistration,
      sourceProjectId: sourceWorkspaceProjectId,
    })
  }
  if (!providedApplicationAuthority && !outcomeIntent) {
    appSpec = withGeneratedApplicationCopyLocale(appSpec, getLocale())
  }
  const sourceWorkspaceTransfer = sourceWorkspace
    ? {
        sourceProjectId: sourceWorkspaceProjectId,
        workspace: sourceWorkspace,
      }
    : null
  if (
    sourceWorkspaceTransfer &&
    (useStudioStore.getState().currentProjectId !==
      sourceWorkspaceTransfer.sourceProjectId ||
      useStudioStore.getState().openPencilSourceWorkspace !==
        sourceWorkspaceTransfer.workspace)
  ) {
    throw new Error(
      'Studio source/design-system generation blocked: the active OpenPencil workspace changed during planning.',
    )
  }
  const generationAuthority = finalizeStudioGenerationAuthority(
    generationAuthorityPreflight,
    fingerprintAppGenerationSpec(appSpec),
  )

  options.onPlanningProgress?.({
    phase: 'done',
    message: livePlannerCreatingProjectMessage(appSpec.name),
  })
  store.setConversation({
    appName: appSpec.name,
    confirmedNarrative: prompt,
    confirmedGoals: [prompt],
    confirmedPages: libraryDesignBinding
      ? (appSpec.pages ?? [])
      : plan.pages,
    confirmedBlocks: (
      libraryDesignBinding ? appSpec.blocks : plan.blocks
    ).map((block) => ({
      ...block,
      binds: block.binds ?? [],
    })),
    themeHint: libraryDesignBinding ? null : plan.themeHint,
    // G2 — conversation carries the USER-signal preset (null when the creator
    // default was auto-resolved); the resolved preset itself is already in
    // the generation context (planning tokens, appSpec, project plan,
    // design_system_preset_id). Library semantics preserved: a library
    // binding is the sole design authority, so conversation stays null.
    designSystemPreset: libraryDesignBinding
      ? null
      : requestedDesignSystemPreset,
    phase: 'generating',
  })

  throwIfGenerationAborted(options.signal)
  // W0-2 D5 — one trace id for this generation's whole create→persist→
  // readback sequence, so a durability incident can be correlated across
  // the Worker's access log without cross-referencing timestamps. The create
  // leg threads it directly (createServerProject → project-api.ts
  // createProject); the persist/readback legs run inside the
  // requestAppGeneration MCP tool handler (register-studio-tools.ts), which
  // is a separate call boundary, so the same id also rides the MCP params
  // below as `_traceId` for that handler to forward into its own
  // loadProject/persistGeneratedBlocks calls.
  const traceId = chatDraftAttempt?.traceId ?? crypto.randomUUID()
  // G1 durability contract — the project is born with
  // `settings.generation = { state:'pending', traceId, startedAt }` so a
  // browser session that dies between create and persist leaves a marked
  // orphan (surfaced by the dashboard) instead of a stub masquerading as a
  // normal draft. The requestAppGeneration handler flips the marker to
  // 'complete' only after the block persist + readback boundary.
  // Keep the finalized authority in the same birth write. The store adapter
  // forwards this field through to project-api alongside the older birth
  // metadata, so there is no follow-up authority patch window.
  const asIsSnapshot = deriveAsIsSnapshotFromPrompt(
    prompt,
    chatDraftAttempt?.startedAt ?? new Date().toISOString(),
    options.originalPastedMarkup,
  )
  const projectBirthOptions = {
    navigate: false,
    activateWorkspace: false,
    traceId,
    generation: buildPendingGenerationMarker(
      traceId,
      chatDraftAttempt ? new Date(chatDraftAttempt.startedAt) : new Date(),
    ),
    // G2 — bind the resolved default preset row to
    // studio_projects.design_system_preset_id at creation time. Only the
    // creator-default resolution carries a row id; explicit developer preset
    // tokens and library/source/outcome authorities do not write the column.
    ...(creatorDefaultDesignSystem?.presetId
      ? { designSystemPresetId: creatorDefaultDesignSystem.presetId }
      : {}),
    // G3 — U2 card lineage rides the same birth-write; the settings merge in
    // createProject keeps it next to (never instead of) settings.generation.
    ...(libraryStructureSelection ? { libraryStructureSelection } : {}),
    // F5 — the verified pack lineage is born WITH the project so the drift
    // comparator can always read the declared identity, even if generation
    // dies before metadata persistence.
    ...(designSystemPackRelease
      ? { designSystemPackRelease: designSystemPackRelease.lineage }
      : {}),
    // R1 — the preview-only ceiling travels WITH the project it produced.
    // The rights declaration pins `productionGenerationAllowed: false`; that
    // stays real only if something downstream can see it, so the lineage is
    // written at birth (not after success) and `publishProject` refuses on it.
    // A browser that dies mid-generation still leaves the label behind.
    ...(projectDesignSystemBinding
      ? {
          projectDesignSystemGeneration: projectDesignSystemBinding.lineage,
        }
      : {}),
    // Intake-card decisions (WonBanking S1-S4/S7 lane) — the structured
    // record of what the person ruled travels from the appSpec into
    // `settings.intakeDecisions` in the SAME birth write, so a generation
    // that dies mid-flight still leaves the decisions readable as data.
    ...(appSpec.intakeDecisions
      ? { intakeDecisions: appSpec.intakeDecisions }
      : {}),
    // 원본 화면(as-is)도 같은 birth write에 — to-be 생성이 중간에 죽어도
    // "무엇에서 출발했는가"는 남아야 한다.
    ...(asIsSnapshot ? { asIsSnapshot } : {}),
    ...(chatDraftPreparation
      ? { chatGeneratedApplicationDraft: chatDraftPreparation }
      : {}),
    generationAuthority,
    generationAuthorityAppSpec: appSpec,
  }
  const projectId = await store.createServerProject(
    appSpec.name,
    undefined,
    projectBirthOptions,
  )
  const projectBoundGenerationAuthority: StudioGenerationAuthorityEnvelope = {
    ...generationAuthority,
    subject: {
      ...generationAuthority.subject,
      projectId,
    },
  }
  let lifecycleController: CandidateGenerationLifecycleController | null = null
  let candidateLifecycleDescriptor:
    | StudioCandidateLifecycleDescriptor
    | undefined
  let projectCleanup: Promise<void> | null = null
  const cleanupCreatedProject = (reasonCode: string): Promise<void> => {
    projectCleanup ??= (async () => {
      const failures: string[] = []
      let lifecycleTerminated = true
      if (lifecycleController) {
        try {
          await lifecycleController.terminate(reasonCode)
        } catch (cause) {
          lifecycleTerminated = false
          failures.push(
            `lifecycle: ${cause instanceof Error ? cause.message : String(cause)}`,
          )
        }
      }
      let projectRemoved = projectId.startsWith('local-')
      if (lifecycleTerminated && !projectRemoved) {
        // "만든 것은 버리지 않는다" — do not throw away what was made. The
        // generation workflow may have already durably written this
        // project's blocks server-side before the overall request failed;
        // deleting here would destroy generated work the user could still
        // open. Deletion is narrowed to the only case that is still safe: a
        // CONFIRMED zero persisted-block count. An unavailable count (the
        // readback itself failed) is treated exactly like "has blocks" —
        // uncertainty must never resolve toward deletion.
        const persistedBlocks = chatDraftPreparation
          ? null
          : await countPersistedBlocks(projectId, traceId).catch(() => null)
        if (persistedBlocks === null || persistedBlocks > 0) {
          // Preserving the project is the CORRECT outcome, not a cleanup
          // failure. Pushing it into `failures` would make this function throw
          // `Generation cleanup failed (…)`, and every caller awaits cleanup
          // before rethrowing its own cause — so that string would REPLACE the
          // real downstream error the user needs to see, and would report a
          // successful safety decision as a fault.
          //
          // The preserved project is reachable — it holds its blocks and opens
          // normally — and the real failure reaches the user through the error
          // this cleanup no longer replaces.
          console.warn(
            '[start-app-generation] cleanup preserved the created project',
            {
              projectId,
              reasonCode,
              traceId,
              persistedBlocks:
                persistedBlocks === null ? 'unavailable' : persistedBlocks,
            },
          )
          // Task 3 (finalization-state design) — the workflow's durable write
          // may already have flipped `settings.generation.state` to
          // 'complete' server-side, in which case the dashboard's orphan/
          // blocked card would otherwise never appear for the very case this
          // branch protects: real generated work, an unmarked draft. Record
          // the browser-side failure onto that marker so the card can render
          // it. `markProjectGenerationFinalizationFailed` re-reads `settings`
          // fresh at write time (never the count read above, and never any
          // earlier snapshot in this flow) precisely because the Cloudflare
          // workflow is racing to write the SAME marker — merging onto a
          // stale snapshot could write back 'pending' and resurrect the
          // orphan mislabeling this whole effort exists to remove.
          //
          // A user-initiated cancellation records NOTHING: the card's copy
          // ("only the last step in your browser didn't complete") would
          // misattribute the user's own cancel to a browser failure, and a
          // cancelled-but-preserved project is just a normal saved app. See
          // `USER_ABORTED_CLEANUP_REASON_CODES`.
          //
          // Non-blocking by design — every caller of `cleanupCreatedProject`
          // AWAITS this whole IIFE before rethrowing the real downstream
          // `cause`. The marker write is a GET then a PATCH, each individually
          // bounded by the 30s SAVE_REQUEST_TIMEOUT_MS, so awaiting it here
          // could hold the user's real error behind up to ~60s of a doomed
          // network round trip on top of the `countPersistedBlocks` read
          // already on this path. The write is best-effort and convergent —
          // if it lands, the dashboard shows the state; if the tab dies
          // first, nothing is lost that was not already lost — so this only
          // STARTS the write and lets cleanup return without waiting on it.
          // `markProjectGenerationFinalizationFailed` already never throws
          // (its own body is wrapped in try/catch and resolves `false` on any
          // failure); the `.catch` below is the belt-and-suspenders guarantee
          // that no future change to that contract can turn this detached
          // call into an unhandled rejection.
          if (!isUserAbortedCleanupReasonCode(reasonCode)) {
            void markProjectGenerationFinalizationFailed(
              projectId,
              reasonCode,
            ).catch((cause) => {
              console.warn(
                '[start-app-generation] Failed to record generation finalization failure',
                { projectId, reasonCode, cause },
              )
            })
          }
        } else {
          const deleted = await deleteServerProject(projectId)
          projectRemoved = deleted
          if (!projectRemoved) {
            failures.push('project: server deletion was not acknowledged')
          }
        }
      }
      if (
        lifecycleTerminated &&
        projectRemoved &&
        useStudioStore.getState().currentProjectId === projectId
      ) {
        useStudioStore.getState().closeProject()
      }
      if (failures.length > 0) {
        const message = `Generation cleanup failed (${failures.join('; ')}).`
        options.onGenerationError?.(message)
        throw new Error(message)
      }
    })()
    return projectCleanup
  }
  if (options.signal?.aborted) {
    await cleanupCreatedProject('generation-request-aborted-before-lifecycle')
    throw generationAbortError()
  }
  if (sourceWorkspaceTransfer) {
    try {
      await bindValidatedSourceWorkspaceToCreatedProject({
        projectId,
        transfer: sourceWorkspaceTransfer,
      })
    } catch (cause) {
      await cleanupCreatedProject('source-workspace-transfer-failed')
      throw cause
    }
  }
  try {
    // The durable job pointer can restore projectId after a real Home reload,
    // but resume admission also requires the server-loaded manifest to prove
    // the exact app subject. Persist that identity before creating the job.
    const appId = appSpec.appId
    if (!appId) {
      throw new Error(
        'Generated application identity requires an exact application subject identifier.',
      )
    }
    await persistGenerationSubjectIdentity(projectId, appId)
  } catch (cause) {
    await cleanupCreatedProject(
      'generation-subject-identity-persistence-failed',
    )
    throw cause
  }
  syncProjectPlanInStore(studioProjectPlan, planningMetadata)
  let candidateLifecycleInput: Omit<
    CandidateGenerationLifecycleInput,
    'projectId' | 'sourceAssessmentRevision' | 'beforeRecordDemand' | 'signal'
  > | null = options.candidateLifecycle ?? null
  if (outcomeIntent) {
    try {
      if (!planningMetadata) {
        throw new Error(
          'Studio outcome generation requires live semantic planner metadata.',
        )
      }
      const candidateId = `candidate-${crypto.randomUUID()}`
      const entryEvidence = await createStudioCandidateEntryEvidence({
        projectId,
        skuId: outcomeIntent.skuId,
        candidateId,
        appName: appSpec.name,
        appClass: studioProjectPlan.appClass,
        targetPlatform: appTargetFromRuntimeTargets(
          studioProjectPlan.runtimeTargets,
        ),
        planningSource: planningMetadata.planningSource,
        pages: plan.pages.map((page) => `${page.id}:${page.route}`),
        blocks: plan.blocks.map((block) => block.name),
      })
      throwIfGenerationAborted(options.signal)
      candidateLifecycleInput = {
        skuId: outcomeIntent.skuId,
        candidateId,
        demandSignalRef: entryEvidence.demandSignalRef,
        previewPlanRef: entryEvidence.previewPlanRef,
        entryEvidence: {
          demandSignal: entryEvidence.demandSignal,
          previewPlan: entryEvidence.previewPlan,
        },
      }
    } catch (cause) {
      await cleanupCreatedProject(
        options.signal?.aborted
          ? 'generation-request-aborted-during-entry-evidence'
          : 'candidate-entry-evidence-failed',
      )
      throw cause
    }
  }
  if (candidateLifecycleInput) {
    const expectedLifecycleBinding = {
      projectId,
      skuId: candidateLifecycleInput.skuId,
      candidateId: candidateLifecycleInput.candidateId ?? null,
      candidateRevision: candidateLifecycleInput.previewPlanRef.contentHash,
      assessmentRevision: sourceSufficiency.assessmentRevision,
    }
    try {
      lifecycleController = await createCandidateGenerationLifecycle({
        ...candidateLifecycleInput,
        projectId,
        sourceAssessmentRevision: sourceSufficiency.assessmentRevision,
        ...(options.signal ? { signal: options.signal } : {}),
        beforeCreateJournal: async (locator) => {
          const descriptor = parseStudioCandidateLifecycleDescriptor({
            schemaVersion: STUDIO_CANDIDATE_LIFECYCLE_DESCRIPTOR_VERSION,
            ...locator,
          })
          assertCandidateLifecycleDescriptorBinding(
            descriptor,
            expectedLifecycleBinding,
          )
          await persistProjectPlan(
            projectId,
            studioProjectPlan,
            planningMetadata,
            descriptor,
            undefined,
            'required',
          )
          candidateLifecycleDescriptor = descriptor
          syncCandidateLifecycleDescriptorInStore(descriptor)
        },
      })
      assertCandidateLifecycleDescriptorBinding(
        lifecycleController,
        expectedLifecycleBinding,
      )
    } catch (cause) {
      const message =
        cause instanceof Error
          ? cause.message
          : 'Candidate lifecycle initialization failed.'
      store.setConversation({ phase: 'gathering' })
      options.onGenerationError?.(message)
      if (cause instanceof CandidateLifecycleAuthorityUncertainError) {
        candidateLifecycleDescriptor = parseStudioCandidateLifecycleDescriptor({
          schemaVersion: STUDIO_CANDIDATE_LIFECYCLE_DESCRIPTOR_VERSION,
          ...cause.locator,
        })
        assertCandidateLifecycleDescriptorBinding(
          candidateLifecycleDescriptor,
          expectedLifecycleBinding,
        )
        syncCandidateLifecycleDescriptorInStore(candidateLifecycleDescriptor)
      } else if (!(cause instanceof CandidateLifecycleTerminationError)) {
        await cleanupCreatedProject(
          options.signal?.aborted
            ? 'generation-request-aborted-during-lifecycle'
            : 'candidate-lifecycle-initialization-failed',
        )
      }
      throw cause instanceof Error ? cause : new Error(message)
    }
  }
  if (options.signal?.aborted) {
    await cleanupCreatedProject('generation-request-aborted-after-lifecycle')
    throw generationAbortError()
  }
  try {
    candidateLifecycleDescriptor ??= lifecycleController
      ? parseStudioCandidateLifecycleDescriptor({
          schemaVersion: STUDIO_CANDIDATE_LIFECYCLE_DESCRIPTOR_VERSION,
          projectId: lifecycleController.projectId,
          skuId: lifecycleController.skuId,
          candidateId: lifecycleController.candidateId,
          journalRunId: lifecycleController.journalRunId,
          candidateRevision: lifecycleController.candidateRevision,
          assessmentRevision: lifecycleController.assessmentRevision,
        })
      : undefined
    if (candidateLifecycleDescriptor) {
      if (candidateLifecycleInput) {
        assertCandidateLifecycleDescriptorBinding(
          candidateLifecycleDescriptor,
          {
            projectId,
            skuId: candidateLifecycleInput.skuId,
            candidateId: candidateLifecycleInput.candidateId ?? null,
            candidateRevision:
              candidateLifecycleInput.previewPlanRef.contentHash,
            assessmentRevision: sourceSufficiency.assessmentRevision,
          },
        )
      }
      syncCandidateLifecycleDescriptorInStore(candidateLifecycleDescriptor)
    }
  } catch (cause) {
    await cleanupCreatedProject('candidate-lifecycle-descriptor-failed')
    throw cause
  }
  let projectPlanPersistence: Promise<void> | null = null
  const persistGenerationMetadata = (
    librarySnapshot?: CogoXyzRegistrySnapshot,
  ): Promise<void> => {
    projectPlanPersistence ??= persistProjectPlan(
      projectId,
      studioProjectPlan,
      planningMetadata,
      candidateLifecycleDescriptor,
      librarySnapshot,
    )
    return projectPlanPersistence
  }
  const shouldRegisterLibraryItem = options.registerLibraryItem !== false
  options.onPlanningProgress?.({
    phase: 'done',
    message: livePlannerStageMessage('generating-payload'),
  })
  await ensureStudioGenerationToolsRegistered()
  if (options.signal?.aborted) {
    await cleanupCreatedProject('generation-request-aborted-before-execution')
    throw generationAbortError()
  }
  const generationStartedAt = Date.now()
  const generationJobBeforeExecution = readStoredAppGenerationJob(projectId)
  let generatedArtifactPersistenceAcknowledged = false
  // 2026-08-17 wave-4 lane-2 -- closes wave-3B's TODO that used to sit on
  // this call (previously "same ungated internal-authority mutation as
  // chat-pipeline-wiring.ts's executeAppGenerationRequest"). The actual
  // provenance check runs at function entry, above -- before any planning
  // work, matching this file's own "a refusal must never cost a full
  // planning wait" rationale (see the Task 4 readiness gate's comment) --
  // not here at the network call itself.
  const generation = Promise.resolve()
    .then(() =>
      mcpBus.execute({
        tool: 'cogo.studio.requestAppGeneration',
        params: {
          appSpec,
          // Reserved caller→handler destination pin. AppSpec remains canonical
          // and routing-free so its persisted authority fingerprint is stable.
          _expectedProjectId: projectId,
          // W0-2 D5 review — carries the create-leg traceId across the MCP
          // call boundary so the handler's persist/readback legs
          // (register-studio-tools.ts) can be correlated with the same id.
          _traceId: traceId,
          ...(chatDraftPreparation
            ? {
                _chatGeneratedApplicationDraftPreparation:
                  chatDraftPreparation,
              }
            : {}),
          // ── Generic Application Ontology Program — wiring lane A1, seam 1 ──
          //
          // The binding the ADMITTED bootstrap constructed, echoed back by the
          // decompose worker on the staged planning observation and already
          // verified against this caller's own five fields
          // (`parseApplicationOntologyGenerationPlanningObservationForBootstrap`).
          // Without this hop it stopped at the plan and the durable job was
          // created unbound. Absent for every ordinary generation, whose
          // payload is therefore unchanged.
          ...(ontologyPlan.applicationOntologyGeneration
            ? {
                [APP_GENERATION_ONTOLOGY_BINDING_PARAM_KEY]:
                  ontologyPlan.applicationOntologyGeneration.binding,
              }
            : {}),
          ...(candidateLifecycleDescriptor
            ? { _candidateLifecycle: candidateLifecycleDescriptor }
            : {}),
          // Internal-only cancellation bridge. The Studio MCP handler owns the
          // streaming AbortController, so the caller signal must be forwarded past
          // planning to stop generation and roll back partial in-memory output.
          ...(options.signal ? { _abortSignal: options.signal } : {}),
        },
      }),
    )
    .then(async (result) => {
      if (!result.success) {
        const message = result.error ?? 'Failed to generate app'
        console.error('[start-app-generation] App generation failed:', message)
        options.onGenerationError?.(message)
        throw new Error(message)
      }
      const generationResult = readRecord(result.data)
      generatedArtifactPersistenceAcknowledged =
        generationResult.status === 'complete'
      if (!generatedArtifactPersistenceAcknowledged) {
        const message =
          'App generation did not acknowledge authenticated project persistence.'
        options.onGenerationError?.(message)
        throw new Error(message)
      }
      if (
        options.signal?.aborted &&
        !generatedArtifactPersistenceAcknowledged
      ) {
        throw generationAbortError()
      }
      if (candidateLifecycleDescriptor) {
        syncCandidateLifecycleDescriptorInStore(candidateLifecycleDescriptor)
      }
      if (lifecycleController) {
        try {
          await lifecycleController.markPreview()
        } catch (cause) {
          const message =
            cause instanceof Error
              ? cause.message
              : 'Candidate preview lifecycle transition failed.'
          store.setConversation({ phase: 'gathering' })
          options.onGenerationError?.(message)
          throw cause instanceof Error ? cause : new Error(message)
        }
      }
      if (!generatedArtifactPersistenceAcknowledged) {
        throwIfGenerationAborted(options.signal)
      }
      if (shouldRegisterLibraryItem) {
        await registerChatStartedAppInLibrary({
          projectId,
          prompt,
          appSpec,
          studioProjectPlan,
          planningMetadata,
          designSystemPreset,
          createdBy: store.user?.id ?? store.user?.email ?? undefined,
          persistToProject: false,
        }).catch((err) => {
          console.warn(
            '[start-app-generation] Failed to register chat-started Library item',
            err,
          )
        })
      }
      if (!generatedArtifactPersistenceAcknowledged) {
        throwIfGenerationAborted(options.signal)
      }
      await persistGenerationMetadata(
        shouldRegisterLibraryItem ? cogoXyzRegistry.snapshot() : undefined,
      )
      if (!generatedArtifactPersistenceAcknowledged) {
        throwIfGenerationAborted(options.signal)
      }
      // Surface the per-app end-user skill only after every authoritative
      // generation side effect has committed. A cancelled request must not
      // leave a success-only chat artifact behind.
      const appSkill = plan.skill?.trim()
      if (appSkill) {
        useStudioStore.getState().appendChatMessage({
          kind: 'app-skill',
          id: `app-skill-${crypto.randomUUID()}`,
          timestamp: Date.now(),
          appName: appSpec.name,
          skill: plan.skill!,
        })
      }
      const completedGenerationJob = readStoredAppGenerationJob(projectId)
      // Navigate while the exact reconnect descriptor still exists. Browser
      // history or shell activation can fail after server persistence; in that
      // case the catch path must retain a real lifecycle finalization subject,
      // not a synthetic card that cannot perform the pending transition.
      navigateToGeneratedStudioProject(projectId)
      if (
        completedGenerationJob &&
        completedGenerationJob.projectId === projectId &&
        completedGenerationJob.appId === appSpec.appId &&
        completedGenerationJob.startedAt >= generationStartedAt &&
        completedGenerationJob.requestId !==
          generationJobBeforeExecution?.requestId
      ) {
        clearStoredAppGenerationJob(
          projectId,
          completedGenerationJob.jobId,
          completedGenerationJob.requestId,
        )
      }
    })
    .catch(async (cause) => {
      store.setConversation({ phase: 'gathering' })
      const storedGenerationJob = readStoredAppGenerationJob(projectId)
      const lifecyclePendingJob = readLifecyclePendingGenerationJob(projectId)
      if (generatedArtifactPersistenceAcknowledged) {
        // requestAppGeneration returns complete only after the authenticated
        // project APIs acknowledge the generated manifest and blocks. From
        // this point onward, aborts and evidence-finalization failures may
        // block verification but must never delete the user's saved app.
        if (lifecyclePendingJob) {
          surfaceLifecyclePendingGenerationJob(lifecyclePendingJob)
        } else if (candidateLifecycleDescriptor) {
          surfaceLifecyclePendingGenerationJob({
            projectId,
            appSpec,
            startedAt: generationStartedAt,
          })
        }
        throw cause
      }
      if (lifecyclePendingJob) {
        // Artifact persistence already committed. Preserve the project and the
        // durable phase so reconnect performs only the idempotent lifecycle
        // transition; deleting or regenerating here could overwrite user work.
        surfaceLifecyclePendingGenerationJob(lifecyclePendingJob)
        throw cause
      }
      if (
        candidateLifecycleDescriptor &&
        storedGenerationJob?.status === 'creating' &&
        !storedGenerationJob.jobId
      ) {
        // A direct lifecycle reservation that never crossed persistence must
        // not survive as a reconnect action that could regenerate empty work.
        clearStoredAppGenerationJob(
          projectId,
          undefined,
          storedGenerationJob.requestId,
        )
      }
      await cleanupCreatedProject(
        options.signal?.aborted ||
          (cause instanceof Error && cause.name === 'AbortError')
          ? 'generation-request-aborted-during-execution'
          : 'generation-execution-failed',
      )
      throw cause
    })

  const candidateLifecycle = lifecycleController
    ? {
        projectId: lifecycleController.projectId,
        skuId: lifecycleController.skuId,
        candidateId: lifecycleController.candidateId,
        journalRunId: lifecycleController.journalRunId,
        candidateRevision: lifecycleController.candidateRevision,
        assessmentRevision: lifecycleController.assessmentRevision,
        verify: async (
          input: Parameters<
            CandidateGenerationLifecycleController['verify']
          >[0],
        ) => {
          await generation
          return lifecycleController.verify(input)
        },
      }
    : undefined

  return {
    status: 'started',
    projectId,
    appSpec,
    generation,
    sourceSufficiency,
    generationAuthority: projectBoundGenerationAuthority,
    ...(candidateLifecycle ? { candidateLifecycle } : {}),
  }
}

async function startAppGenerationWithCanonicalLease(
  prompt: string,
  options: InternalStartAppGenerationOptions,
): Promise<StartAppGenerationOutcome> {
  const isChatGeneration =
    !options.libraryDesignOntologyBinding &&
    !options.libraryRagResolution &&
    (options.generationEntry ?? 'chat') === 'chat'
  // A Chat retry must replay one canonical request through both reservation
  // and decomposition. Non-Chat entry points retain their original prompt
  // contract unchanged.
  const requestContent = isChatGeneration
    ? normalizeChatGenerationRequestContent(prompt)
    : prompt
  const chatGenerationAttemptScope = isChatGeneration
    ? useStudioStore.getState().user?.id
    : undefined
  const leaseId = options.generationLeaseId?.trim() || crypto.randomUUID()
  if (!claimStudioGenerationRequest(leaseId)) {
    throw new StudioGenerationBusyError()
  }
  try {
    const outcome = await startAppGenerationFromPromptWithLease(requestContent, {
      ...options,
      generationLeaseId: leaseId,
      ...(chatGenerationAttemptScope
        ? { chatGenerationAttemptScope }
        : {}),
    })
    if (
      outcome.status === 'clarification-required' ||
      outcome.status === 'generation-readiness-blocked'
    ) {
      // Neither status started a candidate lifecycle, so the lease must be
      // released immediately here — there is no `.generation` promise for
      // either to release it later, unlike the 'started' branch below.
      releaseStudioGenerationRequest(leaseId)
      return outcome
    }
    const attempt = isChatGeneration
      ? chatGenerationDraftAttemptForRequestContent(
          requestContent,
          chatGenerationAttemptScope,
        )
      : null
    return {
      ...outcome,
      generation: outcome.generation
        .then((value) => {
          if (attempt) {
            completeChatGenerationDraftAttempt(
              requestContent,
              attempt.idempotencyKey,
              chatGenerationAttemptScope,
            )
          }
          return value
        })
        .finally(() => {
          releaseStudioGenerationRequest(leaseId)
        }),
    }
  } catch (cause) {
    releaseStudioGenerationRequest(leaseId)
    throw cause
  }
}

export async function startAppGenerationFromPrompt(
  prompt: string,
  options: StartAppGenerationOptions = {},
): Promise<StartAppGenerationOutcome> {
  if (hasUntypedLibraryAuthority(options)) {
    throw rejectStudioLibraryBinding()
  }
  assertApplicationOntologyBootstrapIsPublicOnly(options)
  return startAppGenerationWithCanonicalLease(prompt, options)
}

/**
 * 2026-08-17 wave-4 lane-2 -- a REQUIRED-trigger front door onto
 * `startAppGenerationFromPrompt`, for a caller that wants the type system
 * (not just the runtime, defense-in-depth check inside
 * `startAppGenerationFromPromptWithLease`) to force it to prove genuine
 * classifier-dispatch provenance. `hero-chat-input.tsx` and
 * `studio-composer.tsx` -- the two chat/classifier-driven surfaces
 * wave-3B's TODO (start-app-generation-from-prompt.ts ~:2500) actually
 * named -- mint the trigger next to their `routeStudioCreationRequest` call
 * and set `StartAppGenerationOptions.trigger` directly instead, so their
 * many existing spy-based tests keep intercepting the one shared entry
 * point unchanged; this wrapper is the same guarantee for a new call site
 * that has no such test surface to preserve.
 *
 * `trigger` is REQUIRED here (unlike `StartAppGenerationOptions.trigger`,
 * which stays optional so the many pre-existing, already-authorized
 * programmatic callers -- wizard, dev pack-release trigger, cogo-xyz,
 * library entry points -- are unaffected). A missing or forged trigger
 * fails closed with a distinct `GenerationTriggerProvenanceError` code
 * BEFORE any planning or `mcpBus.execute` work starts; a genuine one
 * delegates unchanged to the real generation pipeline.
 */
export async function startAppGenerationFromClassifiedPrompt(
  prompt: string,
  trigger: GenerationTriggerProvenance,
  options: StartAppGenerationOptions = {},
): Promise<StartAppGenerationOutcome> {
  assertGenuineGenerationTrigger(trigger)
  return startAppGenerationFromPrompt(prompt, { ...options, trigger })
}

export async function startLibraryApplicationGenerationFromPrompt(
  prompt: string,
  options: StartLibraryApplicationGenerationOptions,
): Promise<StartAppGenerationOutcome> {
  const untypedOptions = options as unknown as Record<string, unknown>
  if (
    (untypedOptions.outcomeIntent !== undefined &&
      untypedOptions.outcomeIntent !== null) ||
    (untypedOptions.libraryRagResolution !== undefined &&
      untypedOptions.libraryRagResolution !== null) ||
    (untypedOptions.designSystemPackRelease !== undefined &&
      untypedOptions.designSystemPackRelease !== null) ||
    !options.libraryDesignOntologyBinding
  ) {
    throw rejectStudioLibraryBinding()
  }
  assertStudioLibraryAuthorityIsIsolated(options)
  // `StartLibraryApplicationGenerationOptions` extends the public options, so
  // `applicationOntologyBootstrap` is assignable here too — and a forbidden
  // sibling key that is silently ignored on THIS entry is indistinguishable,
  // from the caller's side, from one that was honoured.
  assertApplicationOntologyBootstrapIsPublicOnly(options)
  return startAppGenerationWithCanonicalLease(prompt, options)
}

export async function startLibraryRagApplicationGenerationFromPrompt(
  prompt: string,
  options: StartLibraryRagApplicationGenerationOptions,
): Promise<StartAppGenerationOutcome> {
  const untypedOptions = options as unknown as Record<string, unknown>
  if (
    (untypedOptions.outcomeIntent !== undefined &&
      untypedOptions.outcomeIntent !== null) ||
    (untypedOptions.libraryDesignOntologyBinding !== undefined &&
      untypedOptions.libraryDesignOntologyBinding !== null) ||
    (untypedOptions.designSystemPackRelease !== undefined &&
      untypedOptions.designSystemPackRelease !== null) ||
    !options.libraryRagResolution
  ) {
    throw rejectStudioLibraryBinding()
  }
  assertStudioLibraryAuthorityIsIsolated(options)
  assertApplicationOntologyBootstrapIsPublicOnly(options)
  return startAppGenerationWithCanonicalLease(prompt, options)
}
